import React, { useState, useMemo, useEffect } from 'react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { getDisplayName } from '../utils/displayHelpers';
import {
  formatCouponUsage,
  formatCouponAssignError,
  isCouponAtLimit,
  hasUsageCountMismatch,
} from '../utils/couponHelpers';
import { 
  Users, 
  Calendar, 
  IndianRupee, 
  TrendingUp, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Star,
  Activity,
  BarChart3,
  PieChart,
  FileText,
  MessageSquare,
  Settings,
  Ban,
  Eye,
  Edit,
  Trash2,
  Download,
  Upload,
  Filter,
  Search,
  RefreshCw,
  Bell,
  Mail,
  Phone,
  CreditCard,
  MapPin,
  Globe,
  Zap,
  Target,
  Award,
  Flag,
  AlertCircle,
  Info,
  Plus,
  X,
  ChevronDown,
  ChevronUp,
  Copy,
  Grid,
  List,
  MoreVertical,
  ToggleLeft,
  ToggleRight,
  Save,
  Tag,
  Palette
} from 'lucide-react';
import BookingCard from '../components/BookingCard';
import TagDisplay from '../components/TagDisplay';
import RoleManagement from '../components/RoleManagement';
import AdvancedAnalytics from '../components/AdvancedAnalytics';
import AdminLayout from '../components/admin/AdminLayout';
import AdminOverview from '../components/admin/AdminOverview';
import AdminThemesPanel from '../components/admin/AdminThemesPanel';
import { AdminIcon, AdminSectionBadge } from '../components/admin/ui/AdminIcon';
import { useTheme } from '../contexts/ThemeContext';
import { DEFAULT_THEME_ID } from '../config/themes';
import '../styles/admin-theme.css';

// Coupon Assignment Component
const CouponAssignment = ({ userId, userEmail, coupons, assignCouponToUser, getUserRedemptions, onEditCoupon }) => {
  const [selectedCoupon, setSelectedCoupon] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [userRedemptions, setUserRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  const selectedCouponMeta = coupons.find((c) => c.code === selectedCoupon);

  useEffect(() => {
    loadUserRedemptions();
    // eslint-disable-next-line
  }, [userId]);

  const loadUserRedemptions = async () => {
    setLoading(true);
    try {
      const result = await getUserRedemptions(userId);
      if (result.success) {
        setUserRedemptions(result.redemptions || []);
      }
    } catch (error) {
      console.error('Error loading redemptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignCoupon = async () => {
    if (!selectedCoupon) {
      setMessage({ type: 'error', text: 'Please select a coupon' });
      return;
    }

    setAssigning(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await assignCouponToUser(userId, selectedCoupon, userEmail);
      if (result.success) {
        setMessage({ type: 'success', text: `Coupon ${selectedCoupon} assigned successfully!` });
        setSelectedCoupon('');
        await loadUserRedemptions();
      } else {
        setMessage({
          type: 'error',
          text: formatCouponAssignError(result.error, selectedCoupon, coupons),
        });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setAssigning(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 8000);
    }
  };

  const activeCoupons = coupons.filter(c => c.active);

  return (
    <div className="space-y-4">
      {/* Assignment UI */}
      <div className="flex gap-2">
        <select
          value={selectedCoupon}
          onChange={(e) => setSelectedCoupon(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
          disabled={assigning}
        >
          <option value="">Select a coupon...</option>
          {activeCoupons.map(coupon => {
            const atLimit = isCouponAtLimit(coupon);
            return (
              <option key={coupon.code} value={coupon.code}>
                {coupon.code} — {formatCouponUsage(coupon)}{atLimit ? ' · LIMIT REACHED' : ''}
              </option>
            );
          })}
        </select>
        <button
          onClick={handleAssignCoupon}
          disabled={!selectedCoupon || assigning}
          className="px-4 py-2 bg-[#0c0f14] text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
        >
          {assigning ? 'Assigning...' : 'Assign'}
        </button>
      </div>

      {selectedCouponMeta && isCouponAtLimit(selectedCouponMeta) && (
        <div className="p-3 rounded-lg bg-amber-50 text-amber-900 border border-amber-200 text-sm">
          <strong>{selectedCouponMeta.code}</strong> is at its limit ({formatCouponUsage(selectedCouponMeta)}).
          {onEditCoupon ? (
            <>
              {' '}
              <button
                type="button"
                onClick={() => onEditCoupon(selectedCouponMeta)}
                className="underline font-semibold hover:text-amber-950"
              >
                Edit coupon
              </button>
              {' '}to clear Max Uses or reset usage count.
            </>
          ) : (
            ' Edit the coupon to clear Max Uses or reset usage count.'
          )}
        </div>
      )}

      {/* Message */}
      {message.text && (
        <div className={`p-3 rounded-lg flex items-start gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          )}
          <span className="text-sm leading-relaxed">{message.text}</span>
        </div>
      )}

      {/* User Redemption History */}
      <div>
        <h5 className="text-sm font-semibold text-gray-700 mb-2">Redemption History:</h5>
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0c0f14]"></div>
          </div>
        ) : userRedemptions.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No coupons redeemed yet</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {userRedemptions.map(redemption => (
              <div key={redemption.id} className="flex items-start justify-between p-3 bg-gradient-to-r from-[#f6f7f5] to-[#eaf3ef] rounded-lg border border-[#dcebe4]">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <code className="px-2 py-0.5 bg-[#eaf3ef] text-[#0c0f14] rounded font-mono font-bold text-sm">
                      {redemption.couponCode}
                    </code>
                    <span className="px-2 py-0.5 bg-[#eaf3ef] text-[#0c0f14] rounded text-xs font-semibold">
                      {redemption.feature}
                    </span>
                    {redemption.adminAssigned && (
                      <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-semibold">
                        Admin Assigned
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-600">
                    <span>
                      <strong>Redeemed:</strong> {new Date(redemption.redeemedAt).toLocaleDateString('en-IN')}
                    </span>
                    <span>
                      <strong>Expires:</strong> {redemption.expiresAt ? new Date(redemption.expiresAt).toLocaleDateString('en-IN') : 'Never'}
                    </span>
                    <span>
                      <strong>Duration:</strong> {redemption.duration === 'lifetime' ? 'Lifetime' : `${redemption.duration} days`}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Feature access is now controlled ONLY via coupons - no manual toggle needed

const AdminDashboard = () => {
  // Hooks must be called at the top level
  const { 
    bookings, caregivers, pets, reviews, offlineJobs, coupons, couponRedemptions, userFeatureAccess, blogs,
    createCoupon, updateCoupon, deleteCoupon, users, fetchUsers, fetchCoupons,
    updateUserSubscriptionPrice, updateUserRole,
    updateUserFeatureAccess, getUserFeatureAccess,
    assignCouponToUser, getUserRedemptions, getCouponUsageDetails, resetCouponUsageCount,
    fetchCouponRedemptions,
    // Admin functions
    suspendUser, activateUser, deleteUser, createUserByAdmin,
    approveBooking, cancelBookingAdmin,
    getPlatformSettings, updatePlatformSettings,
    getFeatureFlags, updateFeatureFlag,
    saveBlogPost, deleteBlogPost, saveFAQ, deleteFAQ,
    getAllTickets, updateTicketStatus
  } = useData();
  const { user } = useAuth();
  const { setPlatformTheme } = useTheme();
  
  // Ensure data arrays are always defined
  const safeCaregivers = caregivers || [];
  const safeBookings = bookings || [];
  const safePets = pets || [];
  const safeUsers = users || [];
  
  const [activeTab, setActiveTab] = useState('overview');
  const [adminSearch, setAdminSearch] = useState('');
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    feature: 'offline_jobs',
    duration: 7,
    durationType: 'days',
    subscriptionTier: '', // free, basic, premium, enterprise
    maxUses: '',
    expiresAt: '',
    description: ''
  });
  const [couponErrors, setCouponErrors] = useState({});
  const [couponSuccess, setCouponSuccess] = useState('');
  const [couponViewMode, setCouponViewMode] = useState('tiles'); // 'tiles' or 'list'
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [showEditCouponModal, setShowEditCouponModal] = useState(false);
  const [viewingCouponRedemptions, setViewingCouponRedemptions] = useState(null);
  const [showRedemptionsModal, setShowRedemptionsModal] = useState(false);
  const [couponRedemptionsList, setCouponRedemptionsList] = useState([]);
  const [loadingRedemptions, setLoadingRedemptions] = useState(false);
  const [resettingCouponCode, setResettingCouponCode] = useState(null);
  
  const redemptionCountByCoupon = useMemo(() => {
    const map = {};
    (couponRedemptions || []).forEach((redemption) => {
      map[redemption.couponCode] = (map[redemption.couponCode] || 0) + 1;
    });
    return map;
  }, [couponRedemptions]);

  // Platform Feature Flags State
  const [featureFlags, setFeatureFlags] = useState({
    'ai-matching': true,
    'realtime-chat': true,
    'video-verification': false,
    'dynamic-pricing': false,
    'insurance-coverage': true
  });

  // Fetch users and coupons on mount (Admin only)
  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchUsers();
      fetchCoupons();
      fetchCouponRedemptions();
      (async () => {
        const result = await getPlatformSettings();
        if (result.success && result.settings) {
          setPlatformSettings((prev) => ({ ...prev, ...result.settings }));
          if (result.settings.defaultThemeId) {
            setPlatformTheme(result.settings.defaultThemeId);
          }
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // State for subscription price editing
  const [editingPriceUserId, setEditingPriceUserId] = useState(null);
  const [editingPrice, setEditingPrice] = useState('');
  const [priceUpdateMessage, setPriceUpdateMessage] = useState({ type: '', text: '' });

  // State for role editing
  const [editingRoleUserId, setEditingRoleUserId] = useState(null);
  const [editingRole, setEditingRole] = useState('');
  const [roleUpdateMessage, setRoleUpdateMessage] = useState({ type: '', text: '' });
  
  // State for create user modal
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    email: '',
    username: '',
    phone: '',
    password: '',
    role: 'owner',
    sendEmail: true,
    // Demographics (optional)
    dateOfBirth: '',
    gender: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    alternatePhone: '',
    emergencyContact: '',
    emergencyContactPhone: '',
    occupation: '',
    nationality: 'Indian'
  });
  const [createUserMessage, setCreateUserMessage] = useState({ type: '', text: '' });
  const [createdUserCredentials, setCreatedUserCredentials] = useState(null);
  
  // State for expandable user rows
  const [expandedUserId, setExpandedUserId] = useState(null);
  
  // State for refresh loading
  const [isRefreshingUsers, setIsRefreshingUsers] = useState(false);
  
  // State for platform settings
  const [platformSettings, setPlatformSettings] = useState({
    commissionRate: 15,
    offlineJobsMonthlyFee: 29,
    minBookingAmount: 500,
    maxBookingAmount: 50000,
    defaultThemeId: DEFAULT_THEME_ID,
  });

  // Toggle feature flag handler
  const toggleFeatureFlag = async (featureKey) => {
    const newValue = !featureFlags[featureKey];
    setFeatureFlags(prev => ({
      ...prev,
      [featureKey]: newValue
    }));
    
    // Save to Firebase
    const result = await updateFeatureFlag(featureKey, newValue);
    if (!result.success) {
      // Revert on error
      setFeatureFlags(prev => ({
        ...prev,
        [featureKey]: !newValue
      }));
      alert(`Failed to update feature flag: ${result.error}`);
    }
  };

  // Handle subscription price update
  const handleUpdateSubscriptionPrice = async (userId) => {
    // Allow empty value to remove subscription (set to null)
    let price = null;
    if (editingPrice && editingPrice.trim() !== '') {
      price = Number(editingPrice);
      if (isNaN(price) || price < 0) {
        setPriceUpdateMessage({ type: 'error', text: 'Please enter a valid price (or leave empty to remove subscription)' });
        return;
      }
    }

    const result = await updateUserSubscriptionPrice(userId, price);
    if (result.success) {
      setPriceUpdateMessage({ type: 'success', text: price ? 'Subscription price updated successfully!' : 'Subscription removed successfully!' });
      setEditingPriceUserId(null);
      setEditingPrice('');
      setExpandedUserId(null); // Auto-collapse
      setTimeout(() => setPriceUpdateMessage({ type: '', text: '' }), 3000);
    } else {
      setPriceUpdateMessage({ type: 'error', text: result.error || 'Failed to update price' });
    }
  };

  const handleUpdateRole = async (userId) => {
    if (!editingRole) {
      setRoleUpdateMessage({ type: 'error', text: 'Please select a valid role' });
      return;
    }

    const result = await updateUserRole(userId, editingRole);
    if (result.success) {
      setRoleUpdateMessage({ type: 'success', text: 'User role updated successfully!' });
      setEditingRoleUserId(null);
      setEditingRole('');
      setExpandedUserId(null); // Auto-collapse
      setTimeout(() => setRoleUpdateMessage({ type: '', text: '' }), 3000);
    } else {
      setRoleUpdateMessage({ type: 'error', text: result.error || 'Failed to update role' });
    }
  };

  const toggleUserExpand = (userId) => {
    setExpandedUserId(expandedUserId === userId ? null : userId);
    // Reset editing states when collapsing
    if (expandedUserId === userId) {
      setEditingPriceUserId(null);
      setEditingRoleUserId(null);
      setEditingPrice('');
      setEditingRole('');
    }
  };

  // Refresh users with loading state
  const handleRefreshUsers = async () => {
    setIsRefreshingUsers(true);
    await fetchUsers();
    setTimeout(() => setIsRefreshingUsers(false), 500); // Keep spinning for smooth UX
  };

  // Copy credentials to clipboard
  const handleCopyCredentials = () => {
    if (!createdUserCredentials) return;
    const text = `Welcome to PetsFostering!\n\nYour account has been created!\n\nLOGIN CREDENTIALS:\nEmail: ${createdUserCredentials.email}\nTemporary Password: ${createdUserCredentials.tempPassword}\n\nLOGIN HERE:\n${createdUserCredentials.loginUrl}\n\nIMPORTANT:\n- Please change your password after first login\n- Keep these credentials secure\n- Contact admin if you have any questions\n\nYour User ID: ${createdUserCredentials.uid || 'N/A'}\n\nWelcome to our platform!`;
    navigator.clipboard.writeText(text).then(() => {
      alert('Credentials copied! Send this to the user via email.');
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy. Please copy manually.');
    });
  };

  // User Management Handlers
  const handleSuspendUser = async (userId) => {
    const reason = prompt('Reason for suspension:');
    if (!reason) return;
    
    const result = await suspendUser(userId, reason);
    if (result.success) {
      alert(result.message);
      fetchUsers();
    } else {
      alert(`Error: ${result.error}`);
    }
  };

  const handleActivateUser = async (userId) => {
    if (!window.confirm('Are you sure you want to activate this user?')) return;
    
    const result = await activateUser(userId);
    if (result.success) {
      alert(result.message);
      fetchUsers();
    } else {
      alert(`Error: ${result.error}`);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    
    const result = await deleteUser(userId);
    if (result.success) {
      alert(result.message);
      fetchUsers();
    } else {
      alert(`Error: ${result.error}`);
    }
  };

  const handleCreateUser = async () => {
    // Validation
    if (!newUserForm.email || !newUserForm.password) {
      setCreateUserMessage({ type: 'error', text: 'Email and password are required' });
      return;
    }

    if (newUserForm.password.length < 6) {
      setCreateUserMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }

    // Prepare user data
    const userData = {
      email: newUserForm.email,
      password: newUserForm.password,
      username: newUserForm.username,
      phone: newUserForm.phone,
      role: newUserForm.role,
      sendEmail: newUserForm.sendEmail,
      // Demographics (only include if provided)
      ...(newUserForm.dateOfBirth && { dateOfBirth: newUserForm.dateOfBirth }),
      ...(newUserForm.gender && { gender: newUserForm.gender }),
      ...(newUserForm.address && { address: newUserForm.address }),
      ...(newUserForm.city && { city: newUserForm.city }),
      ...(newUserForm.state && { state: newUserForm.state }),
      ...(newUserForm.pincode && { pincode: newUserForm.pincode }),
      ...(newUserForm.country && { country: newUserForm.country }),
      ...(newUserForm.alternatePhone && { alternatePhone: newUserForm.alternatePhone }),
      ...(newUserForm.emergencyContact && { emergencyContact: newUserForm.emergencyContact }),
      ...(newUserForm.emergencyContactPhone && { emergencyContactPhone: newUserForm.emergencyContactPhone }),
      ...(newUserForm.occupation && { occupation: newUserForm.occupation }),
      ...(newUserForm.nationality && { nationality: newUserForm.nationality })
    };

    const result = await createUserByAdmin(userData);
    if (result.success) {
      setCreateUserMessage({ type: 'success', text: result.message });
      setCreatedUserCredentials(result.credentials);
      
      // Reset form
      setNewUserForm({
        email: '',
        username: '',
        phone: '',
        password: '',
        role: 'owner',
        sendEmail: true
      });
      
      // Refresh users list
      fetchUsers();
      
      // Don't close modal immediately - show credentials first
    } else {
      setCreateUserMessage({ type: 'error', text: result.error || 'Failed to create user' });
    }
  };

  const closeCreateUserModal = () => {
    setShowCreateUserModal(false);
    setNewUserForm({
      email: '',
      username: '',
      phone: '',
      password: '',
      role: 'owner',
      sendEmail: true,
      // Demographics (optional)
      dateOfBirth: '',
      gender: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
      alternatePhone: '',
      emergencyContact: '',
      emergencyContactPhone: '',
      occupation: '',
      nationality: 'Indian'
    });
    setCreateUserMessage({ type: '', text: '' });
    setCreatedUserCredentials(null);
  };

  // Caregiver Management Handlers removed - no verification needed

  // Booking Management Handlers
  const handleApproveBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to approve this booking?')) return;
    
    const result = await approveBooking(bookingId);
    if (result.success) {
      alert(result.message);
    } else {
      alert(`Error: ${result.error}`);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    const reason = prompt('Reason for cancellation:');
    if (!reason) return;
    
    const refundAmount = prompt('Refund amount (0 for no refund):');
    if (refundAmount === null) return;
    
    const result = await cancelBookingAdmin(bookingId, reason, Number(refundAmount));
    if (result.success) {
      alert(result.message);
    } else {
      alert(`Error: ${result.error}`);
    }
  };

  // Platform Settings Handler
  const handleSavePlatformSettings = async (settings) => {
    const result = await updatePlatformSettings(settings);
    if (result.success) {
      alert(result.message);
    } else {
      alert(`Error: ${result.error}`);
    }
  };

  // Coupon Management Handlers
  const handleToggleCouponStatus = async (couponCode, currentStatus) => {
    try {
      const result = await updateCoupon(couponCode, { active: !currentStatus });
      if (result && result.success) {
        setCouponSuccess(`Coupon "${couponCode}" ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
        await fetchCoupons();
        setTimeout(() => setCouponSuccess(''), 3000);
      } else {
        alert('Error: ' + (result?.error || 'Failed to update coupon'));
      }
    } catch (error) {
      console.error('Error toggling coupon:', error);
      alert('Failed to toggle coupon status');
    }
  };

  const handleEditCoupon = (coupon) => {
    setEditingCoupon({
      code: coupon.code,
      feature: coupon.feature,
      duration: coupon.duration === 'lifetime' ? 30 : coupon.duration,
      durationType: coupon.duration === 'lifetime' ? 'lifetime' : 'days',
      subscriptionTier: coupon.subscriptionTier || '',
      maxUses: coupon.maxUses || '',
      usedCount: coupon.usedCount || 0,
      expiresAt: coupon.expiresAt || '',
      description: coupon.description,
      active: coupon.active
    });
    setShowEditCouponModal(true);
  };

  const handleResetCouponUsage = async (couponCode) => {
    const coupon = coupons.find((c) => c.code === couponCode);
    const storedCount = coupon?.usedCount || 0;
    const redemptionCount = redemptionCountByCoupon[couponCode] ?? couponRedemptionsList.length;

    if (
      !window.confirm(
        `Reset usage count for "${couponCode}"?\n\nStored count: ${storedCount}\nRecorded redemptions: ${redemptionCount}\n\nThis will set usedCount to ${redemptionCount}.`
      )
    ) {
      return;
    }

    setResettingCouponCode(couponCode);
    try {
      const result = await resetCouponUsageCount(couponCode);
      if (result?.success) {
        setCouponSuccess(`Usage count for "${couponCode}" reset to ${result.usedCount}.`);
        if (editingCoupon?.code === couponCode) {
          setEditingCoupon((prev) => ({ ...prev, usedCount: result.usedCount }));
        }
        if (viewingCouponRedemptions === couponCode) {
          await handleViewRedemptions(couponCode);
        }
        setTimeout(() => setCouponSuccess(''), 4000);
      } else {
        alert('Error: ' + (result?.error || 'Failed to reset usage count'));
      }
    } catch (error) {
      console.error('Error resetting coupon usage:', error);
      alert('Failed to reset usage count');
    } finally {
      setResettingCouponCode(null);
    }
  };

  const handleUpdateCoupon = async (e) => {
    e.preventDefault();
    
    if (!editingCoupon) return;

    try {
      const updateData = {
        feature: editingCoupon.feature,
        duration: editingCoupon.durationType === 'lifetime' ? 'lifetime' : parseInt(editingCoupon.duration),
        subscriptionTier: editingCoupon.subscriptionTier || null,
        active: editingCoupon.active,
        maxUses: editingCoupon.maxUses ? parseInt(editingCoupon.maxUses) : null,
        expiresAt: editingCoupon.expiresAt || null,
        description: editingCoupon.description
      };

      const result = await updateCoupon(editingCoupon.code, updateData);
      
      if (result && result.success) {
        setCouponSuccess(`Coupon "${editingCoupon.code}" updated successfully!`);
        setShowEditCouponModal(false);
        setEditingCoupon(null);
        await fetchCoupons();
        setTimeout(() => setCouponSuccess(''), 3000);
      } else {
        alert('Error: ' + (result?.error || 'Failed to update coupon'));
      }
    } catch (error) {
      console.error('Error updating coupon:', error);
      alert('Failed to update coupon');
    }
  };

  const handleDeleteCoupon = async (couponCode) => {
    if (!window.confirm(`Are you sure you want to delete coupon "${couponCode}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const result = await deleteCoupon(couponCode);
      if (result && result.success) {
        setCouponSuccess(`Coupon "${couponCode}" deleted successfully!`);
        await fetchCoupons();
        setTimeout(() => setCouponSuccess(''), 3000);
      } else {
        alert('Error: ' + (result?.error || 'Failed to delete coupon'));
      }
    } catch (error) {
      console.error('Error deleting coupon:', error);
      alert('Failed to delete coupon');
    }
  };

  const handleViewRedemptions = async (couponCode) => {
    setLoadingRedemptions(true);
    setViewingCouponRedemptions(couponCode);
    setShowRedemptionsModal(true);
    
    try {
      const result = await getCouponUsageDetails(couponCode);
      if (result && result.success) {
        setCouponRedemptionsList(result.redemptions || []);
      } else {
        setCouponRedemptionsList([]);
      }
    } catch (error) {
      console.error('Error loading redemptions:', error);
      setCouponRedemptionsList([]);
    } finally {
      setLoadingRedemptions(false);
    }
  };

  const getCouponRedemptionTotal = (couponCode) =>
    redemptionCountByCoupon[couponCode] ?? 0;

  const renderCouponUsageStatus = (coupon) => {
    const redemptionCount = getCouponRedemptionTotal(coupon.code);
    const atLimit = isCouponAtLimit(coupon);
    const mismatch = hasUsageCountMismatch(coupon, redemptionCount);

    if (!atLimit && !mismatch) return null;

    return (
      <div className="flex flex-wrap gap-2 mb-3">
        {atLimit && (
          <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">
            LIMIT REACHED ({formatCouponUsage(coupon)})
          </span>
        )}
        {mismatch && (
          <span className="px-2 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800">
            Count mismatch — {coupon.usedCount || 0} stored / {redemptionCount} redemptions
          </span>
        )}
      </div>
    );
  };

  const renderResetUsageButton = (couponCode, className = '') => {
    const coupon = coupons.find((c) => c.code === couponCode);
    if (!coupon) return null;

    const redemptionCount = getCouponRedemptionTotal(couponCode);
    const showReset = isCouponAtLimit(coupon) || hasUsageCountMismatch(coupon, redemptionCount);
    if (!showReset) return null;

    return (
      <button
        type="button"
        onClick={() => handleResetCouponUsage(couponCode)}
        disabled={resettingCouponCode === couponCode}
        className={`w-full px-3 py-2 bg-amber-100 text-amber-900 hover:bg-amber-200 rounded-lg transition-colors font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 ${className}`}
      >
        <RefreshCw className={`w-4 h-4 ${resettingCouponCode === couponCode ? 'animate-spin' : ''}`} />
        {resettingCouponCode === couponCode ? 'Resetting...' : 'Reset usage count'}
      </button>
    );
  };

  const handleCouponChange = (e) => {
    const { name, value } = e.target;
    setNewCoupon(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (couponErrors[name]) {
      setCouponErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateCoupon = () => {
    const errors = {};
    
    if (!newCoupon.code.trim()) {
      errors.code = 'Coupon code is required';
    } else if (newCoupon.code.length < 4) {
      errors.code = 'Coupon code must be at least 4 characters';
    } else if (!/^[A-Z0-9]+$/.test(newCoupon.code)) {
      errors.code = 'Coupon code must contain only uppercase letters and numbers';
    } else if (coupons.some(c => c.code === newCoupon.code)) {
      errors.code = 'This coupon code already exists';
    }

    if (!newCoupon.description.trim()) {
      errors.description = 'Description is required';
    }

    if (newCoupon.durationType === 'days' && (!newCoupon.duration || newCoupon.duration <= 0)) {
      errors.duration = 'Duration must be at least 1 day';
    }

    setCouponErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    
    if (!validateCoupon()) {
      return;
    }

    try {
      const couponData = {
        code: newCoupon.code.toUpperCase(),
        feature: newCoupon.feature,
        duration: newCoupon.durationType === 'lifetime' ? 'lifetime' : parseInt(newCoupon.duration),
        subscriptionTier: newCoupon.subscriptionTier || null,
        active: true,
        maxUses: newCoupon.maxUses ? parseInt(newCoupon.maxUses) : null,
        expiresAt: newCoupon.expiresAt || null,
        description: newCoupon.description,
        createdBy: user.id || user.uid,
        createdAt: new Date().toISOString()
      };

      console.log('Creating coupon:', couponData);
      
      const result = await createCoupon(couponData);
      
      if (result && result.success) {
        const durationText = couponData.duration === 'lifetime' ? 'Lifetime' : `${couponData.duration} days`;
        const tierText = couponData.subscriptionTier ? ` (${couponData.subscriptionTier} tier)` : '';
        setCouponSuccess(`Coupon "${couponData.code}" created successfully! ${durationText}${tierText}`);
        setShowCouponForm(false);
        setNewCoupon({
          code: '',
          feature: 'offline_jobs',
          duration: 7,
          durationType: 'days',
          subscriptionTier: '',
          maxUses: '',
          expiresAt: '',
          description: ''
        });
        setCouponErrors({});
      } else {
        setCouponErrors({ submit: result?.error || 'Failed to create coupon' });
      }
      
      setTimeout(() => {
        setCouponSuccess('');
        setShowCouponForm(false);
      }, 2000);
    } catch (error) {
      setCouponErrors({ submit: 'Failed to create coupon. Please try again.' });
    }
  };

  // Comprehensive business analytics - moved outside try block
  const analytics = useMemo(() => {
    const totalBookings = safeBookings.length;
    const totalRevenue = safeBookings.reduce((sum, booking) => sum + ((booking.escrowAmount || 0) * 0.15), 0);
    const totalGMV = safeBookings.reduce((sum, booking) => sum + (booking.escrowAmount || 0), 0);
    const activeBookings = safeBookings.filter(b => b.status === 'in_progress').length;
    const pendingBookings = safeBookings.filter(b => b.status === 'requested').length;
    const completedBookings = safeBookings.filter(b => b.status === 'completed').length;
    const cancelledBookings = safeBookings.filter(b => b.status === 'cancelled').length;
    
    const totalUsers = safeUsers.length;
    const totalCaregivers = safeUsers.filter(u => u.role === 'caregiver').length;
    const activeCaregivers = safeCaregivers.filter(c => c.calendarAvailability?.length > 0).length;
    
    const averageRating = reviews?.length > 0 ? 
      (reviews.reduce((sum, review) => sum + (review.score || 0), 0) / reviews.length).toFixed(1) : 0;
    const totalReviews = reviews?.length || 0;
    
    // Success rates
    const bookingSuccessRate = totalBookings > 0 ? ((completedBookings / totalBookings) * 100).toFixed(1) : 0;
    
    // Revenue per booking
    const avgRevenuePerBooking = totalBookings > 0 ? (totalRevenue / totalBookings).toFixed(0) : 0;
    const avgGMVPerBooking = totalBookings > 0 ? (totalGMV / totalBookings).toFixed(0) : 0;
    
    // Offline Jobs stats
    const totalOfflineJobs = offlineJobs?.length || 0;
    const totalOfflineRevenue = offlineJobs?.reduce((sum, job) => sum + job.amount + (job.taxAmount || 0), 0) || 0;
    const offlineJobsAdoptionRate = totalUsers > 0 ? ((userFeatureAccess?.length || 0) / totalUsers * 100).toFixed(1) : 0;
    
    // Coupon stats
    const totalCoupons = coupons?.length || 0;
    const activeCoupons = coupons?.filter(c => c.active).length || 0; // Fixed: use 'active' not 'isActive'
    const totalCouponRedemptions = userFeatureAccess?.length || 0;
    const activeSubscriptions = userFeatureAccess?.filter((access) => access.isActive).length || 0;

    const completedForPayout = safeBookings.filter((b) => b.status === 'completed');
    const caregiverPayoutMap = completedForPayout.reduce((acc, booking) => {
      const caregiverId = booking.caregiverId;
      if (!caregiverId) return acc;
      const caregiverShare = (booking.escrowAmount || 0) * 0.85;
      if (!acc[caregiverId]) {
        acc[caregiverId] = {
          id: caregiverId,
          name: booking.caregiver?.name || booking.caregiver?.displayName || 'Caregiver',
          amount: 0,
          jobs: 0,
          status: 'pending',
          date: booking.end || booking.updatedAt || booking.createdAt,
        };
      }
      acc[caregiverId].amount += caregiverShare;
      acc[caregiverId].jobs += 1;
      return acc;
    }, {});

    const caregiverPayouts = Object.values(caregiverPayoutMap);
    const pendingPayoutsTotal = caregiverPayouts.reduce((sum, payout) => sum + payout.amount, 0);
    const pendingPayoutCaregivers = caregiverPayouts.length;

    const recentTransactions = [...safeBookings]
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      .slice(0, 10)
      .map((booking) => ({
        id: booking.id,
        date: booking.createdAt,
        type: 'Booking',
        user: booking.owner?.name || booking.owner?.displayName || 'User',
        amount: booking.escrowAmount || 0,
        status: booking.status || 'pending',
      }));
    
    return {
      totalBookings,
      totalRevenue,
      totalGMV,
      activeBookings,
      pendingBookings,
      completedBookings,
      cancelledBookings,
      totalUsers,
      totalCaregivers,
      activeCaregivers,
      averageRating,
      totalReviews,
      bookingSuccessRate,
      avgRevenuePerBooking,
      avgGMVPerBooking,
      totalOfflineJobs,
      totalOfflineRevenue,
      offlineJobsAdoptionRate,
      totalCoupons,
      activeCoupons,
      totalCouponRedemptions,
      activeSubscriptions,
      pendingPayoutsTotal,
      pendingPayoutCaregivers,
      caregiverPayouts,
      recentTransactions,
    };
  }, [safeBookings, safeCaregivers, safePets, safeUsers, reviews, offlineJobs, coupons, userFeatureAccess]);

  const cityStats = useMemo(() => {
    const cityMap = {};
    safeUsers.forEach((u) => {
      const city = (u.city || u.location || 'Unknown').split(',')[0].trim();
      if (!cityMap[city]) cityMap[city] = { city, users: 0, caregivers: 0 };
      cityMap[city].users += 1;
      if (u.role === 'caregiver') cityMap[city].caregivers += 1;
    });
    return Object.values(cityMap).sort((a, b) => b.users - a.users);
  }, [safeUsers]);

  try {

    const adminNavItems = [
      { id: 'overview', label: 'Dashboard', icon: BarChart3 },
      { id: 'bookings', label: 'Bookings', icon: Calendar },
      { id: 'users', label: 'Users', icon: Users },
      { id: 'financial', label: 'Finance', icon: IndianRupee },
      { id: 'analytics', label: 'Analytics', icon: TrendingUp },
      { id: 'roles', label: 'Roles', icon: Shield },
      { id: 'coupons', label: 'Coupons', icon: Target },
      { id: 'offline-jobs', label: 'Offline Jobs', icon: Activity },
      { id: 'support', label: 'Support', icon: MessageSquare },
      { id: 'content', label: 'Content', icon: FileText },
      { id: 'marketing', label: 'Marketing', icon: Globe },
      { id: 'themes', label: 'Themes', icon: Palette },
      { id: 'platform', label: 'Platform', icon: Settings },
      { id: 'system', label: 'System', icon: Zap },
    ];

    const handleAdminPrimaryAction = () => {
      if (activeTab === 'users') {
        setShowCreateUserModal(true);
        return;
      }
      if (activeTab === 'content') {
        setActiveTab('content');
      }
    };

    const primaryActionLabel =
      activeTab === 'users' ? 'Create user' : activeTab === 'bookings' ? 'New booking' : 'Add new';

    return (
    <AdminLayout
      user={user}
      navItems={adminNavItems}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      searchQuery={adminSearch}
      onSearchChange={setAdminSearch}
      onPrimaryAction={['users', 'bookings'].includes(activeTab) ? handleAdminPrimaryAction : undefined}
      primaryActionLabel={primaryActionLabel}
    >
              {activeTab === 'overview' && (
                <AdminOverview
                  analytics={analytics}
                  bookings={safeBookings}
                  navItems={adminNavItems}
                  onViewBookings={() => setActiveTab('bookings')}
                  onNavigate={setActiveTab}
                  onRefresh={handleRefreshUsers}
                />
              )}

              {activeTab !== 'overview' && (
              <div className="admin-bento-tile admin-bento-tile--full p-5 sm:p-6 lg:p-8">

              {activeTab === 'bookings' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">All Bookings</h2>
                  
                  <div className="space-y-4">
                    {bookings.map((booking) => (
                      <BookingCard key={booking.id} booking={booking} showActions={false} />
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'roles' && (
                <RoleManagement 
                  users={safeUsers} 
                  onRoleAssigned={fetchUsers}
                />
              )}

              {activeTab === 'users' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
                      <p className="text-gray-600 mt-1">Create, manage, and monitor all user accounts</p>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowCreateUserModal(true)}
                        className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-md"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create User
                      </button>
                      <button
                        onClick={handleRefreshUsers}
                        disabled={isRefreshingUsers}
                        className="inline-flex items-center px-4 py-2 bg-[#0c0f14] text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshingUsers ? 'animate-spin' : ''}`} />
                        {isRefreshingUsers ? 'Refreshing...' : 'Refresh'}
                      </button>
                    </div>
                  </div>

                  {/* Success/Error Messages */}
                  {priceUpdateMessage.text && (
                    <div className={`p-4 rounded-lg ${
                      priceUpdateMessage.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                    }`}>
                      <div className="flex items-center gap-2">
                        {priceUpdateMessage.type === 'success' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        )}
                        <p className={`text-sm ${priceUpdateMessage.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                          {priceUpdateMessage.text}
                        </p>
                      </div>
                    </div>
                  )}

                  {roleUpdateMessage.text && (
                    <div className={`p-4 rounded-lg ${
                      roleUpdateMessage.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                    }`}>
                      <div className="flex items-center gap-2">
                        {roleUpdateMessage.type === 'success' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        )}
                        <p className={`text-sm ${roleUpdateMessage.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                          {roleUpdateMessage.text}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Consolidated Users Table */}
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    {safeUsers.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <Users className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                        <p className="text-lg font-medium">No users found</p>
                        <p className="text-sm mt-1">Click refresh to load users</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {safeUsers.map((u) => (
                          <div key={u.id} className="hover:bg-gray-50 transition-colors">
                            {/* User Row - Clickable */}
                            <div 
                              onClick={() => toggleUserExpand(u.id)}
                              className="flex items-center justify-between p-4 cursor-pointer"
                            >
                              <div className="flex items-center gap-4 flex-1">
                                <img
                                  src={u.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop'}
                                  alt={u.name || u.email}
                                  className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="font-semibold text-gray-900">{u.name || u.email}</p>
                                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                      u.role === 'admin' ? 'bg-red-100 text-red-800' :
                                      u.role === 'caregiver' ? 'bg-[#eaf3ef] text-[#0c0f14]' :
                                      'bg-green-100 text-green-800'
                                    }`}>
                                      {u.role || 'owner'}
                                    </span>
                                    {u.status === 'suspended' && (
                                      <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 font-medium">
                                        Suspended
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600">{u.email}</p>
                                  {u.phone && <p className="text-xs text-gray-500 mt-0.5">{u.phone}</p>}
                                </div>
                              </div>
                              
                                <div className="flex items-center gap-4">
                                <button
                                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleUserExpand(u.id);
                                  }}
                                >
                                  {expandedUserId === u.id ? (
                                    <ChevronUp className="w-5 h-5 text-gray-600" />
                                  ) : (
                                    <ChevronDown className="w-5 h-5 text-gray-600" />
                                  )}
                                </button>
                              </div>
                            </div>

                            {/* Expanded Section - All Options */}
                            {expandedUserId === u.id && (
                              <div className="bg-gray-50 border-t border-gray-200 p-6 space-y-6">
                                {/* Role Management */}
                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                      <Shield className="w-5 h-5 text-[#0c0f14]" />
                                      <h4 className="font-semibold text-gray-900">User Role</h4>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <select
                                      value={editingRoleUserId === u.id ? editingRole : u.role || 'owner'}
                                      onChange={(e) => {
                                        setEditingRoleUserId(u.id);
                                        setEditingRole(e.target.value);
                                      }}
                                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                                    >
                                      <option value="owner">Pet Owner</option>
                                      <option value="caregiver">Caregiver</option>
                                      <option value="admin">Administrator</option>
                                    </select>
                                    <button
                                      onClick={() => {
                                        if (!editingRoleUserId) {
                                          setEditingRoleUserId(u.id);
                                          setEditingRole(u.role || 'owner');
                                        }
                                        handleUpdateRole(u.id);
                                      }}
                                      className="px-6 py-2 bg-[#0c0f14] text-white rounded-lg hover:opacity-90 transition-colors font-medium"
                                    >
                                      Save Role
                                    </button>
                                  </div>
                                </div>

                                {/* Assign Coupons to User - PRIMARY WAY TO GRANT FEATURE ACCESS */}
                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                      <Tag className="w-5 h-5 text-[#0c0f14]" />
                                      <h4 className="font-semibold text-gray-900">Assign Coupons</h4>
                                    </div>
                                  </div>
                                  
                                  <CouponAssignment 
                                    userId={u.id || u.uid} 
                                    userEmail={u.email}
                                    coupons={coupons}
                                    assignCouponToUser={assignCouponToUser}
                                    getUserRedemptions={getUserRedemptions}
                                    onEditCoupon={handleEditCoupon}
                                  />
                                </div>

                                {/* User Credentials */}
                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                      <Info className="w-5 h-5 text-[#5c6670]" />
                                      <h4 className="font-semibold text-gray-900">User Credentials</h4>
                                    </div>
                                  </div>
                                  <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm text-gray-600">Email:</span>
                                      <span className="text-sm font-mono font-semibold text-gray-900">{u.email || 'N/A'}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm text-gray-600">Username:</span>
                                      <span className="text-sm font-mono font-semibold text-gray-900">{u.username || u.displayName || 'N/A'}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm text-gray-600">User ID:</span>
                                      <span className="text-sm font-mono text-gray-700">{u.id || u.uid || 'N/A'}</span>
                                    </div>
                                    {u.tempPassword && (
                                      <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                                        <span className="text-sm text-gray-600">Temp Password:</span>
                                        <span className="text-sm font-mono font-semibold text-red-600">{u.tempPassword}</span>
                                      </div>
                                    )}
                                    <button
                                      onClick={() => {
                                        const credentialsText = `User Credentials for PetsFostering\n\nEmail: ${u.email}\nUsername: ${u.username || u.displayName || 'N/A'}\nUser ID: ${u.id || u.uid}\n${u.tempPassword ? `\nTemporary Password: ${u.tempPassword}` : '\nNote: No temporary password available.'}\n\nLogin URL: ${window.location.origin}/login\n\nRole: ${u.role || 'owner'}`;
                                        navigator.clipboard.writeText(credentialsText).then(() => {
                                          alert('Credentials copied to clipboard!');
                                        }).catch(err => {
                                          console.error('Failed to copy:', err);
                                          alert('Failed to copy. Please copy manually.');
                                        });
                                      }}
                                      className="w-full px-4 py-2 bg-[#0c0f14] text-white rounded-lg hover:opacity-90 transition-colors font-medium flex items-center justify-center gap-2 mt-2"
                                    >
                                      <Copy className="w-4 h-4" />
                                      Copy Credentials
                                    </button>
                                    {!u.tempPassword && u.createdByAdmin && (
                                      <p className="text-xs text-orange-600 mt-2">
                                        Password not available for security. User needs to reset password if forgotten.
                                      </p>
                                    )}
                                  </div>
                                </div>

                                {/* Actions */}
                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                      <Settings className="w-5 h-5 text-gray-600" />
                                      <h4 className="font-semibold text-gray-900">Account Actions</h4>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    {u.status === 'suspended' ? (
                                      <button
                                        onClick={() => handleActivateUser(u.id)}
                                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                                      >
                                        <CheckCircle className="w-4 h-4 inline mr-2" />
                                        Activate User
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => handleSuspendUser(u.id)}
                                        className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium"
                                      >
                                        <Ban className="w-4 h-4 inline mr-2" />
                                        Suspend User
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleDeleteUser(u.id)}
                                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                                    >
                                      <Trash2 className="w-4 h-4 inline mr-2" />
                                      Delete User
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Create User Modal */}
                  {showCreateUserModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                          <h3 className="text-xl font-bold text-gray-900">Create New User</h3>
                          <button
                            onClick={closeCreateUserModal}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>

                        <div className="p-6 space-y-6">
                          {/* Success/Error Messages */}
                          {createUserMessage.text && (
                            <div className={`p-4 rounded-lg ${
                              createUserMessage.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                            }`}>
                              <div className="flex items-center gap-2">
                                {createUserMessage.type === 'success' ? (
                                  <CheckCircle className="w-5 h-5 text-green-600" />
                                ) : (
                                  <AlertCircle className="w-5 h-5 text-red-600" />
                                )}
                                <p className={`text-sm ${createUserMessage.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                                  {createUserMessage.text}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Show credentials after successful creation */}
                          {createdUserCredentials && (
                            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 space-y-4">
                              <div className="flex items-center gap-2 text-green-800 font-semibold mb-4">
                                <CheckCircle className="w-6 h-6" />
                                User Created Successfully!
                              </div>
                              
                              <div className="bg-white rounded-lg p-4 space-y-3">
                                <p className="text-sm font-medium text-gray-700">Login Credentials:</p>
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between py-2 border-b border-gray-200">
                                    <span className="text-sm text-gray-600">Email:</span>
                                    <span className="text-sm font-mono font-semibold text-gray-900">{createdUserCredentials.email}</span>
                                  </div>
                                  <div className="flex items-center justify-between py-2 border-b border-gray-200">
                                    <span className="text-sm text-gray-600">Temp Password:</span>
                                    <span className="text-sm font-mono font-semibold text-gray-900">{createdUserCredentials.tempPassword}</span>
                                  </div>
                                  <div className="flex items-center justify-between py-2">
                                    <span className="text-sm text-gray-600">Login URL:</span>
                                    <a 
                                      href={createdUserCredentials.loginUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm text-[#5c6670] hover:underline"
                                    >
                                      Open Login Page
                                    </a>
                                  </div>
                                </div>
                              </div>

                              {/* Success Message */}
                              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                                <div className="flex items-start gap-2">
                                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                                  <div className="text-sm text-green-900">
                                    <p className="font-bold mb-2 text-base inline-flex items-center gap-2"><AdminIcon name="check" size="sm" className="text-green-600" /> User created with Firebase Auth</p>
                                    <p className="mb-2 font-semibold">User can now login immediately with these credentials.</p>
                                    <ul className="ml-1 space-y-2">
                                      <li className="flex items-center gap-2"><AdminIcon name="check" size="sm" className="text-green-600 shrink-0" /><strong>Firebase Auth account created</strong></li>
                                      <li className="flex items-center gap-2"><AdminIcon name="check" size="sm" className="text-green-600 shrink-0" /><strong>Firestore user data saved</strong></li>
                                      <li><strong>User can login at:</strong> <span className="font-mono bg-green-200 px-1 rounded">{createdUserCredentials.loginUrl}</span></li>
                                    </ul>
                                  </div>
                                </div>
                              </div>

                              <div className="bg-[#f6f7f5] border border-[#e8ece9] rounded-lg p-4">
                                <div className="flex items-start gap-2">
                                  <Info className="w-5 h-5 text-[#5c6670] flex-shrink-0 mt-0.5" />
                                  <div className="text-sm text-[#0c0f14]">
                                    <p className="font-medium mb-1 inline-flex items-center gap-1.5"><AdminIcon name="mail" size="sm" /> Email these credentials to user</p>
                                    <ul className="list-disc ml-4 space-y-1">
                                      <li><strong>Click "Copy Credentials"</strong> below to copy all details</li>
                                      <li>Email them to the user</li>
                                      <li>User can login immediately</li>
                                      <li>Remind them to change password after first login</li>
                                    </ul>
                                  </div>
                                </div>
                              </div>

                              <div className="flex gap-3">
                                <button
                                  onClick={handleCopyCredentials}
                                  className="flex-1 px-4 py-3 bg-[#0c0f14] text-white rounded-lg hover:opacity-90 transition-colors font-medium flex items-center justify-center gap-2"
                                >
                                  <Copy className="w-5 h-5" />
                                  Copy Credentials
                                </button>
                                <button
                                  onClick={closeCreateUserModal}
                                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                                >
                                  Done
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Form (hidden after successful creation) */}
                          {!createdUserCredentials && (
                            <>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Email * <span className="text-red-500">Required</span>
                                  </label>
                                  <input
                                    type="email"
                                    value={newUserForm.email}
                                    onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                                    placeholder="user@example.com"
                                  />
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Username
                                  </label>
                                  <input
                                    type="text"
                                    value={newUserForm.username}
                                    onChange={(e) => setNewUserForm({ ...newUserForm, username: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                                    placeholder="Optional - defaults to email"
                                  />
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Phone Number
                                  </label>
                                  <input
                                    type="tel"
                                    value={newUserForm.phone}
                                    onChange={(e) => setNewUserForm({ ...newUserForm, phone: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                                    placeholder="+1234567890"
                                  />
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Temporary Password * <span className="text-red-500">Min 6 chars</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={newUserForm.password}
                                    onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                                    placeholder="Temp password for user"
                                  />
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  User Role *
                                </label>
                                <select
                                  value={newUserForm.role}
                                  onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value })}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                                >
                                  <option value="owner">Pet Owner</option>
                                  <option value="caregiver">Caregiver</option>
                                  <option value="admin">Administrator</option>
                                </select>
                              </div>

                              {/* Demographics & Contact Details (Optional) */}
                              <div className="border-t border-gray-200 pt-6">
                                <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                  <Users className="w-4 h-4 text-[#0c0f14]" />
                                  Demographics & Contact Details (Optional)
                                </h4>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Date of Birth
                                    </label>
                                    <input
                                      type="date"
                                      value={newUserForm.dateOfBirth}
                                      onChange={(e) => setNewUserForm({ ...newUserForm, dateOfBirth: e.target.value })}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Gender
                                    </label>
                                    <select
                                      value={newUserForm.gender}
                                      onChange={(e) => setNewUserForm({ ...newUserForm, gender: e.target.value })}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                                    >
                                      <option value="">Select...</option>
                                      <option value="male">Male</option>
                                      <option value="female">Female</option>
                                      <option value="other">Other</option>
                                      <option value="prefer_not_to_say">Prefer not to say</option>
                                    </select>
                                  </div>

                                  <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Full Address
                                    </label>
                                    <textarea
                                      value={newUserForm.address}
                                      onChange={(e) => setNewUserForm({ ...newUserForm, address: e.target.value })}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                                      placeholder="House/Flat No., Street, Area..."
                                      rows={2}
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      City
                                    </label>
                                    <input
                                      type="text"
                                      value={newUserForm.city}
                                      onChange={(e) => setNewUserForm({ ...newUserForm, city: e.target.value })}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                                      placeholder="City"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      State
                                    </label>
                                    <input
                                      type="text"
                                      value={newUserForm.state}
                                      onChange={(e) => setNewUserForm({ ...newUserForm, state: e.target.value })}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                                      placeholder="State/Province"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      PIN Code
                                    </label>
                                    <input
                                      type="text"
                                      value={newUserForm.pincode}
                                      onChange={(e) => setNewUserForm({ ...newUserForm, pincode: e.target.value })}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                                      placeholder="PIN/ZIP Code"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Country
                                    </label>
                                    <input
                                      type="text"
                                      value={newUserForm.country}
                                      onChange={(e) => setNewUserForm({ ...newUserForm, country: e.target.value })}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                                      placeholder="Country"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Alternate Phone
                                    </label>
                                    <input
                                      type="tel"
                                      value={newUserForm.alternatePhone}
                                      onChange={(e) => setNewUserForm({ ...newUserForm, alternatePhone: e.target.value })}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                                      placeholder="+1234567890"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Emergency Contact Name
                                    </label>
                                    <input
                                      type="text"
                                      value={newUserForm.emergencyContact}
                                      onChange={(e) => setNewUserForm({ ...newUserForm, emergencyContact: e.target.value })}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                                      placeholder="Full Name"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Emergency Contact Phone
                                    </label>
                                    <input
                                      type="tel"
                                      value={newUserForm.emergencyContactPhone}
                                      onChange={(e) => setNewUserForm({ ...newUserForm, emergencyContactPhone: e.target.value })}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                                      placeholder="+1234567890"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Occupation
                                    </label>
                                    <input
                                      type="text"
                                      value={newUserForm.occupation}
                                      onChange={(e) => setNewUserForm({ ...newUserForm, occupation: e.target.value })}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                                      placeholder="Job/Profession"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Nationality
                                    </label>
                                    <input
                                      type="text"
                                      value={newUserForm.nationality}
                                      onChange={(e) => setNewUserForm({ ...newUserForm, nationality: e.target.value })}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-transparent"
                                      placeholder="Nationality"
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Feature Access Note */}
                              <div className="bg-gradient-to-r from-[#f6f7f5] to-[#eaf3ef] border border-[#dcebe4] rounded-lg p-4">
                                <p className="text-sm text-gray-700 inline-flex items-start gap-2">
                                  <AdminIcon name="tip" size="sm" className="shrink-0 mt-0.5" />
                                  <span><strong>Tip:</strong> After creating the user, go to Subscriptions tab → Find the user → Assign coupons to grant feature access</span>
                                </p>
                              </div>

                              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={newUserForm.sendEmail}
                                    onChange={(e) => setNewUserForm({ ...newUserForm, sendEmail: e.target.checked })}
                                    className="w-4 h-4 text-[#5c6670] border-gray-300 rounded focus:ring-[#0c0f14]/20"
                                  />
                                  <span className="text-sm text-gray-700">
                                    Send welcome email with login credentials (Manual - copy credentials after creation)
                                  </span>
                                </label>
                              </div>
                            </>
                          )}
                        </div>

                        {!createdUserCredentials && (
                          <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                            <button
                              onClick={closeCreateUserModal}
                              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleCreateUser}
                              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                            >
                              <Plus className="w-4 h-4" />
                              Create User
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'analytics' && (
                <AdvancedAnalytics analytics={analytics} />
              )}

              {activeTab === 'coupons' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-900">Coupon Management</h2>
                    <button
                      onClick={() => {
                        setShowCouponForm(!showCouponForm);
                        if (showCouponForm) {
                          setCouponErrors({});
                          setCouponSuccess('');
                        }
                      }}
                      className={`inline-flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                        showCouponForm 
                          ? 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                          : 'bg-[#0c0f14] text-white hover:opacity-90'
                      }`}
                    >
                      {showCouponForm ? (
                        <>
                          <X className="w-4 h-4 mr-2" />
                          Cancel
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" />
                          Create Coupon
                        </>
                      )}
                    </button>
                  </div>

                  {/* Success Message */}
                  {couponSuccess && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <p className="text-green-800 font-medium">{couponSuccess}</p>
                      </div>
                    </div>
                  )}

                  {/* Coupon Creation Form */}
                  {showCouponForm && (
                    <form onSubmit={handleCreateCoupon} className="bg-white rounded-xl shadow-lg border-2 border-[#dcebe4] p-8">
                      <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">Create New Coupon</h3>
                          <p className="text-sm text-gray-600 mt-1">Configure a new coupon for any platform feature</p>
                        </div>
                      </div>
                      
                      <div className="space-y-6">
                        {/* Row 1: Coupon Code and Feature */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Coupon Code */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">
                              Coupon Code <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="code"
                              value={newCoupon.code}
                              onChange={handleCouponChange}
                              placeholder="e.g., SUMMER2024"
                              className={`w-full px-4 py-3 text-lg border-2 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-[#0c0f14] uppercase font-mono ${
                                couponErrors.code ? 'border-red-500 bg-red-50' : 'border-gray-300'
                              }`}
                            />
                            {couponErrors.code && (
                              <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4" />
                                {couponErrors.code}
                              </p>
                            )}
                            <p className="text-xs text-gray-500 mt-2">Use uppercase letters and numbers only</p>
                          </div>

                          {/* Feature */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">
                              Feature Access <span className="text-red-500">*</span>
                            </label>
                            <select
                              name="feature"
                              value={newCoupon.feature}
                              onChange={handleCouponChange}
                              className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-[#0c0f14]"
                            >
                              <option value="offline_jobs">Offline Jobs Tracking</option>
                              <option value="premium_support">Premium Support</option>
                              <option value="advanced_analytics">Advanced Analytics</option>
                              <option value="priority_listing">Priority Listing</option>
                              <option value="verified_badge">Verified Badge</option>
                              <option value="multi_location">Multi-Location Access</option>
                              <option value="custom_branding">Custom Branding</option>
                              <option value="api_access">API Access</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-2">Select which feature this coupon unlocks</p>
                          </div>
                        </div>

                        {/* Row 2: Duration Settings */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {/* Duration Type */}
                          <div className="md:col-span-1">
                            <label className="block text-sm font-semibold text-gray-900 mb-2">
                              Duration Type <span className="text-red-500">*</span>
                            </label>
                            <select
                              name="durationType"
                              value={newCoupon.durationType}
                              onChange={handleCouponChange}
                              className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-[#0c0f14]"
                            >
                              <option value="days">Time-Limited (Days)</option>
                              <option value="lifetime">Lifetime Access</option>
                            </select>
                          </div>

                          {/* Duration (only for days) */}
                          {newCoupon.durationType === 'days' && (
                            <div className="md:col-span-2">
                              <label className="block text-sm font-semibold text-gray-900 mb-2">
                                Duration (Days) <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="number"
                                name="duration"
                                value={newCoupon.duration}
                                onChange={handleCouponChange}
                                min="1"
                                step="1"
                                placeholder="e.g., 7 for 1 week, 30 for 1 month"
                                className={`w-full px-4 py-3 text-base border-2 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-[#0c0f14] ${
                                  couponErrors.duration ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                }`}
                              />
                              {couponErrors.duration && (
                                <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                                  <AlertTriangle className="w-4 h-4" />
                                  {couponErrors.duration}
                                </p>
                              )}
                              <p className="text-xs text-gray-500 mt-2">Quick: 7 days (trial), 30 days (month), 90 days (quarter), 365 days (year)</p>
                            </div>
                          )}
                        </div>

                        {/* Row 3: Usage Limits */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Max Uses */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">
                              Maximum Uses
                            </label>
                            <input
                              type="number"
                              name="maxUses"
                              value={newCoupon.maxUses}
                              onChange={handleCouponChange}
                              min="1"
                              placeholder="Unlimited"
                              className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-[#0c0f14]"
                            />
                            <p className="text-xs text-gray-500 mt-2">Leave empty for unlimited redemptions</p>
                          </div>

                          {/* Coupon Expiry Date */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">
                              Coupon Expires On
                            </label>
                            <input
                              type="date"
                              name="expiresAt"
                              value={newCoupon.expiresAt}
                              onChange={handleCouponChange}
                              min={new Date().toISOString().split('T')[0]}
                              className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-[#0c0f14]"
                            />
                            <p className="text-xs text-gray-500 mt-2">When the coupon code itself expires (optional)</p>
                          </div>
                        </div>

                        {/* Row 4: Description */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 mb-2">
                            Description <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            name="description"
                            value={newCoupon.description}
                            onChange={handleCouponChange}
                            placeholder="e.g., 1 month access to offline job tracking - Special promotion for early adopters"
                            rows={3}
                            className={`w-full px-4 py-3 text-base border-2 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20 focus:border-[#0c0f14] resize-none ${
                              couponErrors.description ? 'border-red-500 bg-red-50' : 'border-gray-300'
                            }`}
                          />
                          {couponErrors.description && (
                            <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                              <AlertTriangle className="w-4 h-4" />
                              {couponErrors.description}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-2">This description will be shown to users when they redeem the coupon</p>
                        </div>
                      </div>

                      {/* Error Message */}
                      {couponErrors.submit && (
                        <div className="mt-6 p-4 bg-red-50 border-2 border-red-300 rounded-lg">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                            <p className="text-red-700 font-medium">{couponErrors.submit}</p>
                          </div>
                        </div>
                      )}

                      {/* Submit Buttons */}
                      <div className="mt-8 pt-6 border-t border-gray-200 flex flex-col sm:flex-row gap-4">
                        <button
                          type="submit"
                          className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0c0f14] text-white px-8 py-4 rounded-lg hover:opacity-90 font-semibold text-lg transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                        >
                          <CheckCircle className="w-5 h-5" />
                          Create Coupon
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowCouponForm(false);
                            setCouponErrors({});
                            setNewCoupon({
                              code: '',
                              feature: 'offline_jobs',
                              duration: 1,
                              durationType: 'months',
                              maxUses: '',
                              expiresAt: '',
                              description: ''
                            });
                          }}
                          className="sm:w-auto px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-semibold text-lg transition-colors"
                        >
                          Cancel
                        </button>
                      </div>

                      {/* Info Box */}
                      <div className="mt-6 p-4 bg-[#f6f7f5] border border-[#e8ece9] rounded-lg">
                        <div className="flex items-start gap-3">
                          <Info className="w-5 h-5 text-[#5c6670] mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-[#0c0f14]">
                            <p className="font-semibold mb-1">How Coupons Work:</p>
                            <p>Once created, users can redeem this coupon in <strong>Settings → Subscriptions</strong>. The coupon will automatically grant them access to the selected feature based on your configuration. You can track redemptions and usage statistics in the coupon list below.</p>
                          </div>
                        </div>
                      </div>
                    </form>
                  )}

                  {/* Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="card">
                      <p className="text-sm text-gray-600 mb-1">Total Coupons</p>
                      <p className="text-2xl font-bold text-gray-900">{analytics.totalCoupons}</p>
                    </div>
                    <div className="card">
                      <p className="text-sm text-gray-600 mb-1">Active Coupons</p>
                      <p className="text-2xl font-bold text-green-600">{analytics.activeCoupons}</p>
                    </div>
                    <div className="card">
                      <p className="text-sm text-gray-600 mb-1">Total Redemptions</p>
                      <p className="text-2xl font-bold text-[#5c6670]">{analytics.totalCouponRedemptions}</p>
                    </div>
                  </div>

                  {/* View Toggle & Coupon Display */}
                  <div className="space-y-4">
                    {/* View Mode Toggle */}
                    <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600 font-medium">View Mode</p>
                      <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
                        <button
                          onClick={() => setCouponViewMode('tiles')}
                          className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${
                            couponViewMode === 'tiles' 
                              ? 'bg-white text-[#0c0f14] shadow-sm font-semibold' 
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <Grid className="w-4 h-4" />
                          Tiles
                        </button>
                        <button
                          onClick={() => setCouponViewMode('list')}
                          className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${
                            couponViewMode === 'list' 
                              ? 'bg-white text-[#0c0f14] shadow-sm font-semibold' 
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <List className="w-4 h-4" />
                          List
                        </button>
                      </div>
                    </div>

                    {/* Empty State */}
                    {coupons.length === 0 ? (
                      <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-200">
                        <div className="w-16 h-16 bg-[#eaf3ef] rounded-full flex items-center justify-center mx-auto mb-4">
                          <FileText className="w-8 h-8 text-[#0c0f14]" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Coupons Yet</h3>
                        <p className="text-gray-600 mb-6">Create your first coupon to start offering feature access to users.</p>
                        <button
                          onClick={() => setShowCouponForm(true)}
                          className="inline-flex items-center px-6 py-3 bg-[#0c0f14] text-white rounded-lg hover:opacity-90 font-medium transition-colors"
                        >
                          <Plus className="w-5 h-5 mr-2" />
                          Create First Coupon
                        </button>
                      </div>
                    ) : (
                      <>
                        {/* TILE VIEW */}
                        {couponViewMode === 'tiles' && (
                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            {coupons.map(coupon => (
                              <div key={coupon.id} className="bg-gradient-to-br from-white to-[#eaf3ef] rounded-xl p-6 border-2 border-[#dcebe4] shadow-lg hover:shadow-xl transition-all hover:scale-105 relative">
                                {/* Status Badge */}
                                <div className="absolute top-4 right-4">
                                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                    coupon.active ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'
                                  }`}>
                                    {coupon.active ? 'ACTIVE' : 'INACTIVE'}
                                  </span>
                                </div>

                                {/* Coupon Code */}
                                <div className="mb-4 mt-2">
                                  <code className="text-2xl font-bold font-mono text-[#0c0f14] bg-[#eaf3ef] px-4 py-2 rounded-lg inline-block">
                                    {coupon.code}
                                  </code>
                                </div>

                                {/* Description */}
                                <p className="text-sm text-gray-700 mb-4 min-h-[40px]">{coupon.description}</p>

                                {renderCouponUsageStatus(coupon)}

                                {/* Stats Grid */}
                                <div className="grid grid-cols-2 gap-3 mb-4">
                                  <div className="bg-white rounded-lg p-3 border border-[#e8ece9]">
                                    <p className="text-xs text-gray-500 mb-1">Duration</p>
                                    <p className="font-bold text-[#0c0f14]">
                                      {coupon.duration === 'lifetime' ? (<><AdminIcon name="infinity" size="sm" /> Lifetime</>) : `${coupon.duration} days`}
                                    </p>
                                  </div>
                                  <div className="bg-white rounded-lg p-3 border border-[#e8ece9]">
                                    <p className="text-xs text-gray-500 mb-1">Redemptions</p>
                                    <p className="font-bold text-[#0c0f14]">
                                      {formatCouponUsage(coupon)}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {getCouponRedemptionTotal(coupon.code)} recorded
                                    </p>
                                  </div>
                                </div>

                                {/* Feature Tag */}
                                <div className="mb-4">
                                  <span className="inline-block px-3 py-1 bg-[#eaf3ef] text-[#0c0f14] rounded-full text-xs font-semibold">
                                    {coupon.feature}
                                  </span>
                                </div>

                                {/* Action Buttons */}
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handleToggleCouponStatus(coupon.code, coupon.active)}
                                      className={`flex-1 px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                                        coupon.active 
                                          ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' 
                                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                                      }`}
                                    >
                                      {coupon.active ? 'Deactivate' : 'Activate'}
                                    </button>
                                    <button
                                      onClick={() => handleEditCoupon(coupon)}
                                      className="px-3 py-2 bg-[#eaf3ef] text-[#0c0f14] hover:bg-[#dcebe4] rounded-lg transition-colors"
                                      title="Edit Coupon"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteCoupon(coupon.code)}
                                      className="px-3 py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors"
                                      title="Delete Coupon"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                  <button
                                    onClick={() => handleViewRedemptions(coupon.code)}
                                    className="w-full px-3 py-2 bg-gradient-to-r from-[#eaf3ef] to-[#f6f7f5] text-[#0c0f14] hover:from-[#dcebe4] hover:to-[#eaf3ef] rounded-lg transition-colors font-medium text-sm flex items-center justify-center gap-2"
                                  >
                                    <Users className="w-4 h-4" />
                                    View Redemptions ({getCouponRedemptionTotal(coupon.code)})
                                  </button>
                                  {renderResetUsageButton(coupon.code)}
                                </div>

                                {/* Expiry Info */}
                                {coupon.expiresAt && (
                                  <p className="text-xs text-gray-500 mt-3 text-center">
                                    Expires: {new Date(coupon.expiresAt).toLocaleDateString('en-IN')}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* LIST VIEW */}
                        {couponViewMode === 'list' && (
                          <div className="space-y-3">
                            {coupons.map(coupon => (
                              <div key={coupon.id} className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm hover:shadow-md transition-all">
                                <div className="flex items-start justify-between gap-4">
                                  {/* Left Section */}
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                      <code className="px-4 py-2 bg-[#eaf3ef] text-[#0c0f14] rounded-lg font-mono font-bold text-lg">
                                        {coupon.code}
                                      </code>
                                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                        coupon.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                                      }`}>
                                        {coupon.active ? 'Active' : 'Inactive'}
                                      </span>
                                      <span className="px-3 py-1 bg-[#eaf3ef] text-[#0c0f14] rounded-full text-xs font-semibold">
                                        {coupon.feature}
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-700 mb-3">{coupon.description}</p>
                                    {renderCouponUsageStatus(coupon)}
                                    <div className="flex items-center gap-6 text-sm text-gray-600">
                                      <span className="flex items-center gap-1">
                                        <Clock className="w-4 h-4" />
                                        <strong>Duration:</strong> {coupon.duration === 'lifetime' ? (<><AdminIcon name="infinity" size="sm" /> Lifetime</>) : `${coupon.duration} days`}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <Users className="w-4 h-4" />
                                        <strong>Uses:</strong> {formatCouponUsage(coupon)}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <Tag className="w-4 h-4" />
                                        <strong>Recorded:</strong> {getCouponRedemptionTotal(coupon.code)}
                                      </span>
                                      {coupon.expiresAt && (
                                        <span className="flex items-center gap-1">
                                          <Calendar className="w-4 h-4" />
                                          <strong>Expires:</strong> {new Date(coupon.expiresAt).toLocaleDateString('en-IN')}
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Right Section - Actions */}
                                  <div className="flex flex-col gap-2">
                                    <button
                                      onClick={() => handleToggleCouponStatus(coupon.code, coupon.active)}
                                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${
                                        coupon.active 
                                          ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' 
                                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                                      }`}
                                    >
                                      {coupon.active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                                      {coupon.active ? 'Deactivate' : 'Activate'}
                                    </button>
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => handleEditCoupon(coupon)}
                                        className="flex-1 px-4 py-2 bg-[#eaf3ef] text-[#0c0f14] hover:bg-[#dcebe4] rounded-lg transition-colors flex items-center justify-center gap-2"
                                      >
                                        <Edit className="w-4 h-4" />
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => handleDeleteCoupon(coupon.code)}
                                        className="px-4 py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors"
                                        title="Delete"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                    <button
                                      onClick={() => handleViewRedemptions(coupon.code)}
                                      className="w-full px-4 py-2 bg-gradient-to-r from-[#eaf3ef] to-[#f6f7f5] text-[#0c0f14] hover:from-[#dcebe4] hover:to-[#eaf3ef] rounded-lg transition-colors font-medium text-sm flex items-center justify-center gap-2"
                                    >
                                      <Users className="w-4 h-4" />
                                      View Redemptions ({getCouponRedemptionTotal(coupon.code)})
                                    </button>
                                    {renderResetUsageButton(coupon.code)}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Edit Coupon Modal */}
                  {showEditCouponModal && editingCoupon && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
                          <h3 className="text-2xl font-bold text-gray-900">Edit Coupon: {editingCoupon.code}</h3>
                          <button
                            onClick={() => {
                              setShowEditCouponModal(false);
                              setEditingCoupon(null);
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <X className="w-6 h-6" />
                          </button>
                        </div>
                        
                        <form onSubmit={handleUpdateCoupon} className="p-6 space-y-6">
                          {/* Active Status Toggle */}
                          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-semibold text-gray-900">Coupon Status</p>
                              <p className="text-sm text-gray-600">Control whether users can redeem this coupon</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setEditingCoupon(prev => ({ ...prev, active: !prev.active }))}
                              className={`px-6 py-3 rounded-lg font-bold transition-colors ${
                                editingCoupon.active 
                                  ? 'bg-green-500 text-white hover:bg-green-600' 
                                  : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                              }`}
                            >
                              {editingCoupon.active ? 'ACTIVE' : 'INACTIVE'}
                            </button>
                          </div>

                          {/* Feature */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">
                              Feature Access
                            </label>
                            <select
                              value={editingCoupon.feature}
                              onChange={(e) => setEditingCoupon(prev => ({ ...prev, feature: e.target.value }))}
                              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20"
                            >
                              <option value="offline_jobs">Offline Jobs Tracking</option>
                              <option value="premium_support">Premium Support</option>
                              <option value="advanced_analytics">Advanced Analytics</option>
                            </select>
                          </div>

                          {/* Duration */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold text-gray-900 mb-2">Duration Type</label>
                              <select
                                value={editingCoupon.durationType}
                                onChange={(e) => setEditingCoupon(prev => ({ ...prev, durationType: e.target.value }))}
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20"
                              >
                                <option value="months">Time-Limited</option>
                                <option value="lifetime">Lifetime</option>
                              </select>
                            </div>
                            {editingCoupon.durationType === 'months' && (
                              <div>
                                <label className="block text-sm font-semibold text-gray-900 mb-2">Duration (Months)</label>
                                <input
                                  type="number"
                                  value={editingCoupon.duration}
                                  onChange={(e) => setEditingCoupon(prev => ({ ...prev, duration: e.target.value }))}
                                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20"
                                  min="0.1"
                                  step="0.1"
                                />
                              </div>
                            )}
                          </div>

                          {/* Usage tracking */}
                          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                            <div>
                              <p className="font-semibold text-gray-900">Usage tracking</p>
                              <p className="text-sm text-gray-700 mt-1">
                                Stored usedCount: <strong>{editingCoupon.usedCount || 0}</strong>
                                {' · '}
                                Recorded redemptions: <strong>{getCouponRedemptionTotal(editingCoupon.code)}</strong>
                                {' · '}
                                Limit: <strong>{editingCoupon.maxUses ? editingCoupon.maxUses : 'Unlimited'}</strong>
                              </p>
                              {isCouponAtLimit({ ...editingCoupon, usedCount: editingCoupon.usedCount || 0, maxUses: editingCoupon.maxUses ? Number(editingCoupon.maxUses) : null }) && (
                                <p className="text-sm text-red-700 mt-2">
                                  This coupon cannot be assigned until you clear Max Uses or reset the usage count.
                                </p>
                              )}
                              {hasUsageCountMismatch(
                                { usedCount: editingCoupon.usedCount || 0 },
                                getCouponRedemptionTotal(editingCoupon.code)
                              ) && (
                                <p className="text-sm text-amber-800 mt-2">
                                  Counts are out of sync — likely from a failed assignment. Reset to match redemption records.
                                </p>
                              )}
                            </div>
                            {renderResetUsageButton(editingCoupon.code)}
                          </div>

                          {/* Max Uses */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">Maximum Uses</label>
                            <input
                              type="number"
                              value={editingCoupon.maxUses}
                              onChange={(e) => setEditingCoupon(prev => ({ ...prev, maxUses: e.target.value }))}
                              placeholder="Leave empty for unlimited"
                              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20"
                              min="1"
                            />
                            <p className="text-xs text-gray-500 mt-2">
                              Leave empty for unlimited admin assignments. For lifetime coupons like OFFLINEFOREVER, unlimited is recommended.
                            </p>
                          </div>

                          {/* Description */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">Description</label>
                            <textarea
                              value={editingCoupon.description}
                              onChange={(e) => setEditingCoupon(prev => ({ ...prev, description: e.target.value }))}
                              rows={3}
                              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0c0f14]/20"
                            />
                          </div>

                          {/* Submit Buttons */}
                          <div className="flex gap-3 pt-4">
                            <button
                              type="submit"
                              className="flex-1 px-6 py-3 bg-[#0c0f14] text-white rounded-lg hover:opacity-90 font-semibold flex items-center justify-center gap-2"
                            >
                              <Save className="w-5 h-5" />
                              Save Changes
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowEditCouponModal(false);
                                setEditingCoupon(null);
                              }}
                              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  )}

                  {/* View Redemptions Modal */}
                  {showRedemptionsModal && viewingCouponRedemptions && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
                          <div>
                            <h3 className="text-2xl font-bold text-gray-900">Redemption History</h3>
                            <p className="text-sm text-gray-600 mt-1">Coupon: <code className="px-2 py-1 bg-[#eaf3ef] text-[#0c0f14] rounded font-mono font-bold">{viewingCouponRedemptions}</code></p>
                          </div>
                          <button
                            onClick={() => {
                              setShowRedemptionsModal(false);
                              setViewingCouponRedemptions(null);
                              setCouponRedemptionsList([]);
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <X className="w-6 h-6" />
                          </button>
                        </div>
                        
                        <div className="p-6">
                          {loadingRedemptions ? (
                            <div className="flex items-center justify-center py-12">
                              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0c0f14]"></div>
                            </div>
                          ) : couponRedemptionsList.length === 0 ? (
                            <div className="text-center py-12">
                              <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                              <p className="text-lg font-semibold text-gray-900">No Redemptions Yet</p>
                              <p className="text-sm text-gray-600 mt-2">This coupon hasn&apos;t been redeemed by any users</p>
                              {(() => {
                                const viewingCoupon = coupons.find((c) => c.code === viewingCouponRedemptions);
                                if (!viewingCoupon || !(viewingCoupon.usedCount > 0)) return null;
                                return (
                                  <div className="mt-6 mx-auto max-w-lg p-4 bg-amber-50 border border-amber-200 rounded-lg text-left">
                                    <p className="text-sm text-amber-900">
                                      <strong>Usage count mismatch:</strong> stored usedCount is{' '}
                                      <strong>{viewingCoupon.usedCount}</strong>, but there are no redemption records.
                                      This blocks new assignments when max uses is reached.
                                    </p>
                                    <div className="mt-3">
                                      {renderResetUsageButton(viewingCouponRedemptions, 'w-auto inline-flex px-4')}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          ) : (
                            <>
                              <div className="mb-4 p-4 bg-gradient-to-r from-[#f6f7f5] to-[#eaf3ef] rounded-lg border border-[#dcebe4]">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm text-gray-600">Total Redemptions</p>
                                    <p className="text-3xl font-bold text-[#0c0f14]">{couponRedemptionsList.length}</p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-gray-600">Admin Assigned</p>
                                    <p className="text-3xl font-bold text-yellow-600">{couponRedemptionsList.filter(r => r.adminAssigned).length}</p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-gray-600">User Redeemed</p>
                                    <p className="text-3xl font-bold text-green-600">{couponRedemptionsList.filter(r => !r.adminAssigned).length}</p>
                                  </div>
                                </div>
                              </div>

                              <div className="overflow-x-auto">
                                <table className="w-full">
                                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                                    <tr>
                                      <th className="text-left p-3 text-sm font-semibold text-gray-900">User</th>
                                      <th className="text-left p-3 text-sm font-semibold text-gray-900">Feature</th>
                                      <th className="text-left p-3 text-sm font-semibold text-gray-900">Redeemed On</th>
                                      <th className="text-left p-3 text-sm font-semibold text-gray-900">Expires</th>
                                      <th className="text-left p-3 text-sm font-semibold text-gray-900">Duration</th>
                                      <th className="text-left p-3 text-sm font-semibold text-gray-900">Source</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200">
                                    {couponRedemptionsList.map(redemption => (
                                      <tr key={redemption.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="p-3">
                                          <div>
                                            <p className="font-medium text-gray-900">{redemption.userEmail}</p>
                                            <p className="text-xs text-gray-500">{redemption.userId}</p>
                                          </div>
                                        </td>
                                        <td className="p-3">
                                          <span className="px-2 py-1 bg-[#eaf3ef] text-[#0c0f14] rounded text-xs font-semibold">
                                            {redemption.feature}
                                          </span>
                                        </td>
                                        <td className="p-3 text-sm text-gray-700">
                                          {new Date(redemption.redeemedAt).toLocaleString('en-IN')}
                                        </td>
                                        <td className="p-3 text-sm text-gray-700">
                                          {redemption.expiresAt 
                                            ? new Date(redemption.expiresAt).toLocaleDateString('en-IN')
                                            : <span className="text-[#0c0f14] font-semibold">Never</span>}
                                        </td>
                                        <td className="p-3 text-sm text-gray-700">
                                          {redemption.duration === 'lifetime' ? 'Lifetime' : `${redemption.duration} days`}
                                        </td>
                                        <td className="p-3">
                                          {redemption.adminAssigned ? (
                                            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-semibold">
                                              👤 Admin
                                            </span>
                                          ) : (
                                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                                              User
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'offline-jobs' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">Offline Jobs Analytics</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="card">
                      <p className="text-sm text-gray-600 mb-1">Total Offline Jobs</p>
                      <p className="text-2xl font-bold text-gray-900">{analytics.totalOfflineJobs}</p>
                    </div>
                    <div className="card">
                      <p className="text-sm text-gray-600 mb-1">Total Revenue Tracked</p>
                      <p className="text-2xl font-bold text-green-600">₹{analytics.totalOfflineRevenue.toLocaleString()}</p>
                    </div>
                    <div className="card">
                      <p className="text-sm text-gray-600 mb-1">Adoption Rate</p>
                      <p className="text-2xl font-bold text-[#5c6670]">{analytics.offlineJobsAdoptionRate}%</p>
                      <p className="text-xs text-gray-500 mt-1">{analytics.totalCouponRedemptions} users active</p>
                    </div>
                  </div>

                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Offline Jobs</h3>
                    <div className="space-y-3">
                      {offlineJobs.length === 0 ? (
                        <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                          No offline jobs recorded yet.
                        </div>
                      ) : offlineJobs.slice(-10).reverse().map(job => (
                        <div key={job.id} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{job.serviceType} - {job.petName}</p>
                              <p className="text-sm text-gray-600">By User ID: {job.userId}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-gray-900">₹{(job.amount + (job.taxAmount || 0)).toLocaleString()}</p>
                              <p className="text-xs text-gray-500">{new Date(job.jobDate).toLocaleDateString('en-IN')}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* FINANCIAL TAB */}
              {activeTab === 'financial' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">Financial Management</h2>
                    <AdminSectionBadge icon="money" className="bg-green-100 text-green-700">Revenue Operations</AdminSectionBadge>
                  </div>

                  {/* Revenue Overview - KPI Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="relative overflow-hidden bg-gradient-to-br from-[#0c0f14] via-[#0c0f14] to-[#0c0f14] rounded-2xl p-5 shadow-lg">
                      <div className="flex items-center gap-2 text-white mb-4">
                        <IndianRupee className="w-4 h-4" />
                        <span className="text-sm font-semibold">Total Revenue</span>
                      </div>
                      <div className="text-4xl font-bold text-white mb-2 tracking-tight">₹{analytics.totalRevenue.toLocaleString('en-IN')}</div>
                      <div className="text-sm text-white/90 font-medium">Platform commission from bookings</div>
                    </div>

                    <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-700 rounded-2xl p-5 shadow-lg">
                      <div className="flex items-center gap-2 text-white mb-4">
                        <Activity className="w-4 h-4" />
                        <span className="text-sm font-semibold">Platform GMV</span>
                      </div>
                      <div className="text-4xl font-bold text-white mb-2 tracking-tight">₹{analytics.totalGMV.toLocaleString('en-IN')}</div>
                      <div className="text-sm text-white/90 font-medium">Gross merchandise value</div>
                    </div>

                    <div className="relative overflow-hidden bg-gradient-to-br from-[#0c0f14] via-[#0c0f14] to-[#0c0f14] rounded-2xl p-5 shadow-lg">
                      <div className="flex items-center gap-2 text-white mb-4">
                        <BarChart3 className="w-4 h-4" />
                        <span className="text-sm font-semibold">Avg Revenue/Booking</span>
                      </div>
                      <div className="text-4xl font-bold text-white mb-2 tracking-tight">₹{analytics.avgRevenuePerBooking}</div>
                      <div className="text-sm text-white/90 font-medium">Per booking revenue</div>
                    </div>

                    <div className="relative overflow-hidden bg-gradient-to-br from-[#0c0f14] via-[#0c0f14] to-[#0c0f14] rounded-2xl p-5 shadow-lg">
                      <div className="flex items-center gap-2 text-white mb-4">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm font-semibold">Total Bookings</span>
                      </div>
                      <div className="text-4xl font-bold text-white mb-2 tracking-tight">{analytics.totalBookings || 0}</div>
                      <div className="text-sm text-white/90 font-medium">{analytics.completedBookings} completed</div>
                    </div>

                    <div className="relative overflow-hidden bg-gradient-to-br from-amber-500 via-amber-600 to-amber-700 rounded-2xl p-5 shadow-lg">
                      <div className="flex items-center gap-2 text-white mb-4">
                        <CreditCard className="w-4 h-4" />
                        <span className="text-sm font-semibold">Pending Payouts</span>
                      </div>
                      <div className="text-4xl font-bold text-white mb-2 tracking-tight">₹{Math.round(analytics.pendingPayoutsTotal).toLocaleString('en-IN')}</div>
                      <div className="text-sm text-white/90 font-medium">{analytics.pendingPayoutCaregivers} caregivers awaiting payout</div>
                    </div>

                    <div className="relative overflow-hidden bg-gradient-to-br from-rose-500 via-rose-600 to-rose-700 rounded-2xl p-5 shadow-lg">
                      <div className="flex items-center gap-2 text-white mb-4">
                        <Star className="w-4 h-4" />
                        <span className="text-sm font-semibold">Subscriptions</span>
                      </div>
                      <div className="text-4xl font-bold text-white mb-2 tracking-tight">{analytics.activeSubscriptions}</div>
                      <div className="text-sm text-white/90 font-medium">Active feature subscriptions</div>
                    </div>
                  </div>

                  {/* Payout Management */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-[#0c0f14]" />
                      Caregiver Payouts
                    </h3>
                    <div className="space-y-3">
                      {analytics.caregiverPayouts.length === 0 ? (
                        <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                          No pending caregiver payouts. Payouts appear here after bookings are completed.
                        </div>
                      ) : analytics.caregiverPayouts.map((payout) => (
                        <div key={payout.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{payout.name}</p>
                              <p className="text-sm text-gray-600">{payout.jobs} completed bookings</p>
                              {payout.date && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Due: {new Date(payout.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-gray-900">₹{Math.round(payout.amount).toLocaleString('en-IN')}</p>
                              <span className="inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 bg-yellow-100 text-yellow-700">
                                ⏳ Pending
                              </span>
                              <button className="mt-2 px-4 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors">
                                Process Payment
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Commission Settings */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Settings className="w-5 h-5 text-[#0c0f14]" />
                      Commission Configuration
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 border-2 border-gray-200 rounded-lg">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Platform Commission (%)</label>
                        <input 
                          type="number" 
                          value={platformSettings.commissionRate}
                          onChange={(e) => setPlatformSettings(prev => ({ ...prev, commissionRate: Number(e.target.value) }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg" 
                        />
                        <p className="text-xs text-gray-500 mt-1">Default commission on all bookings</p>
                      </div>
                      <div className="p-4 border-2 border-gray-200 rounded-lg">
                        <label className="block text-sm font-medium text-gray-700 mb-2">External Jobs Fee (₹/month)</label>
                        <input 
                          type="number" 
                          value={platformSettings.offlineJobsMonthlyFee}
                          onChange={(e) => setPlatformSettings(prev => ({ ...prev, offlineJobsMonthlyFee: Number(e.target.value) }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg" 
                        />
                        <p className="text-xs text-gray-500 mt-1">Monthly subscription for external job tracking</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => handleSavePlatformSettings(platformSettings)}
                      className="mt-4 px-6 py-2 bg-[#0c0f14] text-white rounded-lg hover:opacity-90 transition-colors"
                    >
                      Save Changes
                    </button>
                  </div>

                  {/* Transaction History */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Transactions</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Date</th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Type</th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">User</th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Amount</th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {analytics.recentTransactions.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                                No transactions yet. Booking activity will appear here.
                              </td>
                            </tr>
                          ) : analytics.recentTransactions.map((txn) => (
                            <tr key={txn.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {txn.date ? new Date(txn.date).toLocaleDateString('en-IN') : '—'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">{txn.type}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">{txn.user}</td>
                              <td className="px-4 py-3 text-sm font-semibold text-green-600">
                                +₹{Math.round(txn.amount).toLocaleString('en-IN')}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  txn.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {txn.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'themes' && (
                <AdminThemesPanel />
              )}

              {/* PLATFORM TAB */}
              {activeTab === 'platform' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">Platform Settings</h2>
                    <AdminSectionBadge icon="settings" className="bg-[#eaf3ef] text-[#0c0f14]">System Configuration</AdminSectionBadge>
                  </div>

                  {/* System Health */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="card bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                      <div className="flex items-center justify-between mb-2">
                        <Activity className="w-8 h-8 text-green-600" />
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <p className="text-sm text-gray-600 mb-1">Platform Status</p>
                      <p className="text-xl font-bold text-green-600">Operational</p>
                      <p className="text-xs text-gray-500 mt-1">Live production site</p>
                    </div>
                    <div className="card bg-gradient-to-br from-[#f6f7f5] to-[#eaf3ef] border-[#e8ece9]">
                      <div className="flex items-center justify-between mb-2">
                        <Users className="w-8 h-8 text-[#5c6670]" />
                      </div>
                      <p className="text-sm text-gray-600 mb-1">Registered Users</p>
                      <p className="text-2xl font-bold text-gray-900">{analytics.totalUsers}</p>
                    </div>
                    <div className="card bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200">
                      <div className="flex items-center justify-between mb-2">
                        <Zap className="w-8 h-8 text-yellow-600" />
                      </div>
                      <p className="text-sm text-gray-600 mb-1">API Response</p>
                      <p className="text-2xl font-bold text-gray-900">—</p>
                      <p className="text-xs text-gray-500 mt-1">Not monitored</p>
                    </div>
                    <div className="card bg-gradient-to-br from-[#f6f7f5] to-[#eaf3ef] border-red-200">
                      <div className="flex items-center justify-between mb-2">
                        <AlertTriangle className="w-8 h-8 text-red-600" />
                      </div>
                      <p className="text-sm text-gray-600 mb-1">Open Issues</p>
                      <p className="text-2xl font-bold text-gray-900">0</p>
                    </div>
                  </div>

                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <AdminIcon name="palette" size="lg" />
                      Platform Theme
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Manage color themes for admin and all users in the dedicated Themes section.
                    </p>
                    <button
                      type="button"
                      onClick={() => setActiveTab('themes')}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0c0f14] text-white text-sm font-semibold hover:opacity-90"
                    >
                      Open Themes
                    </button>
                  </div>

                  {/* Feature Flags */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Zap className="w-5 h-5 text-[#0c0f14]" />
                      Feature Flags & Toggles
                    </h3>
                    <div className="space-y-4">
                      {[
                        { key: 'ai-matching', name: 'AI-Powered Matching', description: 'Smart algorithm to match pets with caregivers' },
                        { key: 'realtime-chat', name: 'Real-time Chat', description: 'Enable live messaging between users' },
                        { key: 'video-verification', name: 'Video Verification', description: 'Video KYC for caregiver onboarding' },
                        { key: 'dynamic-pricing', name: 'Dynamic Pricing', description: 'Surge pricing during peak demand' },
                        { key: 'insurance-coverage', name: 'Insurance Coverage', description: 'Pet insurance for bookings' }
                      ].map((feature) => (
                        <div key={feature.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-gray-900">{feature.name}</p>
                              {featureFlags[feature.key] && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                  Active
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{feature.description}</p>
                          </div>
                          <button
                            onClick={() => toggleFeatureFlag(feature.key)}
                            className={`relative inline-flex h-8 w-16 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#0c0f14]/20 focus:ring-offset-2 ${
                              featureFlags[feature.key] ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                            title={`Click to ${featureFlags[feature.key] ? 'disable' : 'enable'} ${feature.name}`}
                          >
                            <span
                              className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform duration-300 ${
                                featureFlags[feature.key] ? 'translate-x-9' : 'translate-x-1'
                              }`}
                            >
                              {featureFlags[feature.key] ? (
                                <svg className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              )}
                            </span>
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    {/* Save Changes Button */}
                    <div className="mt-6 p-4 bg-[#f6f7f5] border border-[#e8ece9] rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Info className="w-5 h-5 text-[#5c6670]" />
                          <p className="text-sm text-[#0c0f14]">
                            Changes are saved automatically. Features are enabled/disabled in real-time.
                          </p>
                        </div>
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                          Live
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Geographic Coverage */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-[#0c0f14]" />
                      Geographic Coverage
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {cityStats.length === 0 ? (
                        <div className="col-span-full p-8 text-center text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                          No user location data yet. City stats will appear as users add their profiles.
                        </div>
                      ) : cityStats.map((city) => (
                        <div key={city.city} className="p-4 border-2 border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-semibold text-gray-900">{city.city}</p>
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#eaf3ef] text-[#0c0f14]">
                              active
                            </span>
                          </div>
                          <div className="space-y-1">
                            <p className="text-sm text-gray-600">{city.users} users</p>
                            <p className="text-sm text-gray-600">{city.caregivers} caregivers</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Security & Compliance */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-[#0c0f14]" />
                      Security & Compliance
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <span className="text-sm text-gray-900">SSL Certificate</span>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <span className="text-sm text-gray-900">GDPR Compliant</span>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <span className="text-sm text-gray-900">Data Encryption</span>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <span className="text-sm text-gray-900">Payment Gateway</span>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <span className="text-sm text-gray-900">Backup System</span>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                          <span className="text-sm text-gray-900">2FA Enforcement</span>
                          <Clock className="w-5 h-5 text-yellow-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* CONTENT TAB */}
              {activeTab === 'content' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">Content Management</h2>
                    <AdminSectionBadge icon="edit" className="bg-[#eaf3ef] text-[#0c0f14]">Editorial Control</AdminSectionBadge>
                  </div>

                  {/* Content Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="card bg-gradient-to-br from-[#f6f7f5] to-[#eaf3ef] border-[#dcebe4]">
                      <FileText className="w-8 h-8 text-[#0c0f14] mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Blog Posts</p>
                      <p className="text-2xl font-bold text-gray-900">{blogs?.length || 0}</p>
                    </div>
                    <div className="card bg-gradient-to-br from-[#f6f7f5] to-[#eaf3ef] border-[#e8ece9]">
                      <Star className="w-8 h-8 text-[#5c6670] mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Pet Profiles</p>
                      <p className="text-2xl font-bold text-gray-900">{pets?.length || 0}</p>
                      <p className="text-xs text-[#5c6670] mt-1">Active profiles</p>
                    </div>
                    <div className="card bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200">
                      <MessageSquare className="w-8 h-8 text-yellow-600 mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Reviews</p>
                      <p className="text-2xl font-bold text-gray-900">{analytics.totalReviews}</p>
                      <p className="text-xs text-yellow-600 mt-1">Avg {analytics.averageRating}★</p>
                    </div>
                    <div className="card bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                      <Globe className="w-8 h-8 text-green-600 mb-2" />
                      <p className="text-sm text-gray-600 mb-1">SEO Score</p>
                      <p className="text-2xl font-bold text-gray-900">—</p>
                      <p className="text-xs text-gray-500 mt-1">Not configured</p>
                    </div>
                  </div>

                  {/* Content Moderation */}
                  <div className="card">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Shield className="w-5 h-5 text-[#0c0f14]" />
                        Content Moderation Queue
                      </h3>
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">0 pending</span>
                    </div>
                    <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                      No items in the moderation queue.
                    </div>
                  </div>

                  {/* Blog Management */}
                  <div className="card">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Blog Posts</h3>
                      <button className="px-4 py-2 bg-[#0c0f14] text-white rounded-lg hover:opacity-90 transition-colors flex items-center gap-2">
                        <Plus className="w-4 h-4" />
                        New Post
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                        No blog posts yet. Create your first post to engage pet owners and caregivers.
                      </div>
                    </div>
                  </div>

                  {/* SEO Management */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Target className="w-5 h-5 text-[#0c0f14]" />
                      SEO & Meta Tags
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Homepage Title</label>
                        <input
                          type="text"
                          defaultValue="PetsFostering - Find Trusted Pet Caregivers Near You"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Meta Description</label>
                        <textarea
                          rows={3}
                          defaultValue="Connect with verified pet caregivers in your area. Safe, reliable, and affordable pet care services."
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <button className="px-6 py-2 bg-[#0c0f14] text-white rounded-lg hover:opacity-90 transition-colors">
                        Update SEO Settings
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* MARKETING TAB */}
              {activeTab === 'marketing' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">Marketing & Growth</h2>
                    <AdminSectionBadge icon="chart" className="bg-[#eaf3ef] text-[#0c0f14]">Campaign Manager</AdminSectionBadge>
                  </div>

                  {/* Marketing Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="card bg-gradient-to-br from-[#f6f7f5] to-[#eaf3ef] border-[#dcebe4]">
                      <Users className="w-8 h-8 text-[#0c0f14] mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Registered Users</p>
                      <p className="text-2xl font-bold text-gray-900">{analytics.totalUsers}</p>
                    </div>
                    <div className="card bg-gradient-to-br from-[#f6f7f5] to-[#eaf3ef] border-[#dcebe4]">
                      <TrendingUp className="w-8 h-8 text-[#0c0f14] mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Booking Success Rate</p>
                      <p className="text-2xl font-bold text-gray-900">{analytics.bookingSuccessRate}%</p>
                    </div>
                    <div className="card bg-gradient-to-br from-[#f6f7f5] to-[#eaf3ef] border-[#e8ece9]">
                      <Target className="w-8 h-8 text-[#5c6670] mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Active Coupons</p>
                      <p className="text-2xl font-bold text-gray-900">{analytics.activeCoupons}</p>
                    </div>
                    <div className="card bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200">
                      <IndianRupee className="w-8 h-8 text-yellow-600 mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Platform Revenue</p>
                      <p className="text-2xl font-bold text-gray-900">₹{Math.round(analytics.totalRevenue).toLocaleString('en-IN')}</p>
                    </div>
                  </div>

                  {/* Active Campaigns */}
                  <div className="card">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Marketing Campaigns</h3>
                      <button className="px-4 py-2 bg-[#0c0f14] text-white rounded-lg hover:opacity-90 transition-colors flex items-center gap-2">
                        <Plus className="w-4 h-4" />
                        New Campaign
                      </button>
                    </div>
                    <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                      No marketing campaigns yet. Launch your first campaign to track reach and conversions.
                    </div>
                  </div>

                  {/* Email Marketing */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Mail className="w-5 h-5 text-[#0c0f14]" />
                      Email Marketing
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="p-3 bg-[#f6f7f5] rounded-lg">
                        <p className="text-sm text-gray-600">Subscribers</p>
                        <p className="text-xl font-bold text-gray-900">0</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-sm text-gray-600">Open Rate</p>
                        <p className="text-xl font-bold text-gray-900">—</p>
                      </div>
                      <div className="p-3 bg-[#f6f7f5] rounded-lg">
                        <p className="text-sm text-gray-600">Click Rate</p>
                        <p className="text-xl font-bold text-gray-900">—</p>
                      </div>
                    </div>
                    <button className="w-full px-4 py-2 bg-[#0c0f14] text-white rounded-lg hover:opacity-90 transition-colors">
                      Create Email Campaign
                    </button>
                  </div>

                  {/* Referral Program */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Award className="w-5 h-5 text-[#0c0f14]" />
                      Referral Program Stats
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 border-2 border-gray-200 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Total Referrals</p>
                        <p className="text-2xl font-bold text-gray-900">0</p>
                      </div>
                      <div className="p-4 border-2 border-gray-200 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Successful Signups</p>
                        <p className="text-2xl font-bold text-gray-900">0</p>
                      </div>
                      <div className="p-4 border-2 border-gray-200 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Rewards Paid</p>
                        <p className="text-2xl font-bold text-gray-900">₹0</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* SUPPORT TAB */}
              {activeTab === 'support' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">Customer Support</h2>
                    <AdminSectionBadge icon="support" className="bg-[#eaf3ef] text-[#0c0f14]">Help Desk</AdminSectionBadge>
                  </div>

                  {/* Support Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="card bg-gradient-to-br from-[#f6f7f5] to-[#eaf3ef] border-red-200">
                      <AlertCircle className="w-8 h-8 text-red-600 mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Open Tickets</p>
                      <p className="text-2xl font-bold text-gray-900">0</p>
                    </div>
                    <div className="card bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200">
                      <Clock className="w-8 h-8 text-yellow-600 mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Avg Response Time</p>
                      <p className="text-2xl font-bold text-gray-900">—</p>
                    </div>
                    <div className="card bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                      <CheckCircle className="w-8 h-8 text-green-600 mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Resolution Rate</p>
                      <p className="text-2xl font-bold text-gray-900">—</p>
                    </div>
                    <div className="card bg-gradient-to-br from-[#f6f7f5] to-[#eaf3ef] border-[#e8ece9]">
                      <Star className="w-8 h-8 text-[#5c6670] mb-2" />
                      <p className="text-sm text-gray-600 mb-1">CSAT Score</p>
                      <p className="text-2xl font-bold text-gray-900">{analytics.averageRating || '—'}</p>
                      <p className="text-xs text-[#5c6670] mt-1">{analytics.totalReviews} reviews</p>
                    </div>
                  </div>

                  {/* Support Tickets */}
                  <div className="card">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Active Support Tickets</h3>
                      <div className="flex gap-2">
                        <button className="px-3 py-1 bg-red-100 text-red-700 rounded-lg text-sm font-medium">
                          Critical (0)
                        </button>
                        <button className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-lg text-sm font-medium">
                          High (0)
                        </button>
                        <button className="px-3 py-1 bg-[#eaf3ef] text-[#0c0f14] rounded-lg text-sm font-medium">
                          Medium (0)
                        </button>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                        No support tickets yet.
                      </div>
                    </div>
                  </div>

                  {/* FAQ Management */}
                  <div className="card">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">FAQ Management</h3>
                      <button className="px-4 py-2 bg-[#0c0f14] text-white rounded-lg hover:opacity-90 transition-colors flex items-center gap-2">
                        <Plus className="w-4 h-4" />
                        Add FAQ
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                        No FAQs yet. Add common questions to help users get started.
                      </div>
                    </div>
                  </div>

                  {/* Live Chat Status */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-[#0c0f14]" />
                      Live Chat Support
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 border-2 border-gray-200 bg-gray-50 rounded-lg">
                        <p className="text-sm font-medium text-gray-700 mb-2">Online Agents</p>
                        <p className="text-2xl font-bold text-gray-900">—</p>
                      </div>
                      <div className="p-4 border-2 border-gray-200 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-2">Active Chats</p>
                        <p className="text-2xl font-bold text-gray-900">0</p>
                      </div>
                      <div className="p-4 border-2 border-[#dcebe4] bg-[#f6f7f5] rounded-lg">
                        <p className="text-sm text-gray-600 mb-2">Avg Wait Time</p>
                        <p className="text-2xl font-bold text-gray-900">—</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* SYSTEM TAB */}
              {activeTab === 'system' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">System Management</h2>
                    <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
                      All Systems Operational
                    </span>
                  </div>

                  {/* Cache Management */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Zap className="w-5 h-5 text-[#0c0f14]" />
                      Cache Management
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Clear browser cache and Firebase offline persistence to resolve loading issues or see fresh data.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <button
                        onClick={() => {
                          localStorage.clear();
                          alert('Local Storage cleared successfully!');
                        }}
                        className="p-6 border-2 border-[#e8ece9] bg-[#f6f7f5] rounded-lg hover:bg-[#eaf3ef] transition-colors text-left group"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="w-12 h-12 bg-[#eaf3ef] rounded-lg flex items-center justify-center group-hover:bg-[#dcebe4] transition-colors">
                            <Trash2 className="w-6 h-6 text-[#5c6670]" />
                          </div>
                          <RefreshCw className="w-5 h-5 text-[#5c6670]" />
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-1">Clear Local Storage</h4>
                        <p className="text-sm text-gray-600">Remove all locally stored data</p>
                      </button>

                      <button
                        onClick={async () => {
                          try {
                            const databases = await window.indexedDB.databases();
                            for (const db of databases) {
                              window.indexedDB.deleteDatabase(db.name);
                            }
                            alert('IndexedDB cleared successfully! Please refresh the page.');
                          } catch (err) {
                            alert('Failed to clear IndexedDB: ' + err.message);
                          }
                        }}
                        className="p-6 border-2 border-[#dcebe4] bg-[#f6f7f5] rounded-lg hover:bg-[#eaf3ef] transition-colors text-left group"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="w-12 h-12 bg-[#eaf3ef] rounded-lg flex items-center justify-center group-hover:bg-[#dcebe4] transition-colors">
                            <Trash2 className="w-6 h-6 text-[#0c0f14]" />
                          </div>
                          <RefreshCw className="w-5 h-5 text-[#5c6670]" />
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-1">Clear Firebase Cache</h4>
                        <p className="text-sm text-gray-600">Remove Firebase offline data (IndexedDB)</p>
                      </button>

                      <button
                        onClick={() => {
                          if (window.confirm('This will reload the page and clear all caches. Continue?')) {
                            localStorage.clear();
                            sessionStorage.clear();
                            window.location.reload(true);
                          }
                        }}
                        className="p-6 border-2 border-red-200 bg-red-50 rounded-lg hover:bg-red-100 transition-colors text-left group"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200 transition-colors">
                            <Zap className="w-6 h-6 text-red-600" />
                          </div>
                          <RefreshCw className="w-5 h-5 text-red-400" />
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-1">Hard Refresh</h4>
                        <p className="text-sm text-gray-600">Clear all caches and reload</p>
                      </button>
                    </div>

                    <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex gap-3">
                        <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-yellow-900 mb-1">Important Notes</p>
                          <ul className="text-sm text-yellow-800 space-y-1">
                            <li>• Cache clearing affects only this browser/device</li>
                            <li>• All users will need to clear their own caches</li>
                            <li>• Data in Firebase remains untouched</li>
                            <li>• Page will reload after clearing cache</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* System Info */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Info className="w-5 h-5 text-[#0c0f14]" />
                      System Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">Firebase Project</p>
                        <p className="font-semibold text-gray-900">petfostering-7917d</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">App Version</p>
                        <p className="font-semibold text-gray-900">v1.0.0</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">Build Date</p>
                        <p className="font-semibold text-gray-900">{new Date().toLocaleDateString()}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">Environment</p>
                        <p className="font-semibold text-gray-900">
                          {window.location.hostname === 'localhost' ? 'Development' : 'Production'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Browser Info */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Globe className="w-5 h-5 text-[#0c0f14]" />
                      Browser Information
                    </h3>
                    <div className="space-y-3">
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 flex justify-between">
                        <span className="text-sm text-gray-600">User Agent</span>
                        <span className="text-sm font-medium text-gray-900 max-w-md truncate">
                          {navigator.userAgent}
                        </span>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 flex justify-between">
                        <span className="text-sm text-gray-600">Browser</span>
                        <span className="text-sm font-medium text-gray-900">
                          {navigator.userAgentData?.brands?.[0]?.brand || 'Unknown'}
                        </span>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 flex justify-between">
                        <span className="text-sm text-gray-600">Platform</span>
                        <span className="text-sm font-medium text-gray-900">{navigator.platform}</span>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 flex justify-between">
                        <span className="text-sm text-gray-600">Language</span>
                        <span className="text-sm font-medium text-gray-900">{navigator.language}</span>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 flex justify-between">
                        <span className="text-sm text-gray-600">Online Status</span>
                        <span className={`text-sm font-medium inline-flex items-center gap-1.5 ${navigator.onLine ? 'text-green-600' : 'text-red-600'}`}>
                          <AdminIcon name={navigator.onLine ? 'online' : 'offline'} size="sm" />
                          {navigator.onLine ? 'Online' : 'Offline'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              </div>
              )}
    </AdminLayout>
  );

  } catch (error) {
    console.error('AdminDashboard Error:', error);
    return (
      <div className="admin-shell min-h-screen flex items-center justify-center p-6">
        <div className="admin-panel p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-[#0c0f14] mb-2">Admin dashboard error</h2>
          <p className="text-[#5c6670] mb-4">Something went wrong loading this page.</p>
          <p className="text-sm text-red-600 mb-6">{error.message}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="admin-btn-primary"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
};

export default AdminDashboard;
